/**
 * MapView — the MapLibre canvas plus all the wiring:
 *   - loads the training cells via the chart-source adapter
 *   - mounts the S-52 layer set per cell (quilt)
 *   - reacts to palette / category / group / rotation changes from the store
 *   - tap to select a feature (M1) or invoke the active chartwork tool (M3)
 *   - draws ownship + tracks (M2), course-up bearing (M2)
 *   - draws chartwork plots (M3)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, {
  type Map as MaplibreMap,
  type MapMouseEvent,
  type MapTouchEvent,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useChartPlotterStore } from '@/store';
import { fixUnreliable } from '@/gnss/fixReliable';
import { useAppActiveStore } from '@/store/appActive';
import type { Track, TrackPoint } from '@/ownship';
import { useShellStore, type RingObject } from '@/store/shell';
import { useRoutingStore, nextWaypointId, nextRouteIdFn } from '@/store/routing';
import { useNotesStore, nextNoteId } from '@/store/notes';
import { useRoutePlanStore } from '@/store/routePlan';
import { trainingChartSource,
  getProvider,
  providerHasDepthData,
  providerHasRealWeather,
  providerCoverageBounds,
  resolveProvider,
  quiltCharts,
  chartsInView, boundsIntersect } from '@/chart-sources';
import { loadJson, saveJson } from '@/store/persist';
import { useChartSourcesStore } from '@/store/chartSources';
import { useChartPacksStore } from '@/store/chartPacks';

/**
 * The live map, for DIAGNOSTICS ONLY. fb-msnbeubc-1 ("frozen, I can't scroll",
 * pulses healthy) could not be diagnosed because no report says whether the
 * gesture handlers were enabled at the time; this closes that hole. Set on
 * mount, cleared on teardown, read by the feedback context builder.
 */
let diagMap: maplibregl.Map | null = null;

/** One line of gesture state for a bug report, or null with no map mounted. */
export function gestureDiagLine(): string | null {
  const m = diagMap;
  if (!m) return null;
  try {
    return `dragPan=${m.dragPan.isEnabled() ? 'on' : 'OFF'} scrollZoom=${m.scrollZoom.isEnabled() ? 'on' : 'OFF'} touchZoom=${m.touchZoomRotate.isEnabled() ? 'on' : 'OFF'}`;
  } catch {
    return null;
  }
}
import { useRealWeatherStore } from '@/store/realWeather';
import { useWeatherOverlayStore } from '@/store/weatherOverlay';
import { useWeatherFieldStore, fieldGridAt } from '@/store/weatherField';
import { fieldVariable } from './fieldRamps';
import { ensureWeatherFieldLayer } from './weatherFieldLayer';
import { watchMapCanvas, recordContext } from './canvasHealth';
import { maxForecastH } from '@/store/weatherOverlay';
import { windAtTime } from '@/weather/realWeather';
import { useSavedTracksStore } from '@/store/savedTracks';
import type { CellMeta, ChartFeature } from '@/types';
import { featurePoint, featureTitle } from '@/ui/shell/featureLabel';
import { buildBaseStyle } from './style/baseStyle';
import { isMobileDevice } from './deviceProfile';
import { setActiveMap } from './mapHandle';
import { setChartedDepthQuery, setChartedDepthDetailQuery, setChartedZocQuery } from './chartQuery';
import { orientationBearingDeg, nextOrientation, orientationLabel } from '@/store/orientation';
import {
  addCellToMap,
  cellAnchorLayerId,
  removeCellFromMap,
  applyVisibility,
  restyleCellsForSymbolSet,
  updateDryingCover,
  applyHazardDeclutter,
  applyDepthBands,
  applyDeclutterMarks,
  type LayerVisibilityState,
} from './chartLayers';
import { LAYERS, setDeclutterSafetyDepth, setDeclutterLevel, setDepthShading, setDepthShadingMode } from './style/layers';
import { ensureOwnshipLayer, updateOwnshipLayer, raiseOwnshipLayers, applyOwnVectorColour, type OwnVectorRender } from './ownshipLayer';
import { ensureObjectsLayer } from './objectsLayer';
import { useObjectsStore, nextObjectId } from '@/store/objects';
import { buildDR, buildEP } from '@/chartwork';
import { ensureRouteLayer, type RouteHazardHighlight } from './routeLayer';
import { setChartProbe, getChartProbe } from './chartProbe';
import { useRouteSafetyStore } from '@/store/routeSafety';
import { ensureMarksLayer, type GotoLeg } from './marksLayer';
import { ensureNotesLayer, raiseNotesLayers } from './notesLayer';
import { setDestinationMarker } from './destinationLayer';
import { ensurePilotageLayer, raisePilotageLayers } from './pilotageLayer';
import { updateClearingDepthHighlight } from './clearingDepthLayer';
import { updateContourHighlight } from './contourHighlightLayer';
import { ensureSafetyLayer, updateSafetyLayer } from './safetyLayer';
import { ensureMobSearchAreaLayer, updateMobSearchAreaLayer } from './mobSearchAreaLayer';
import { ensureAnchorWatchLayer, updateAnchorWatchLayer, raiseAnchorWatchLayers, type AnchorWatchLayerInput } from './anchorWatchLayer';
import { ensureTideLayer, updateTideLayer } from './tideLayer';
import { ensureRangeRingsLayer, updateRangeRingsLayer } from './rangeRingsLayer';
import { autoRingIntervalNm } from '@/store/rangeRings';
import { rhumbDistanceNm, formatBearing, formatDistance } from '@/nav';
import { ensureEblVrmLayer, updateEblVrmLayer } from './eblVrmLayer';
import { ensurePilotagePlanLayer, updatePilotagePlanLayer, raisePilotagePlanLayers } from './pilotagePlanLayer';
import { attachPlanInteractions, handlePlanClick } from './pilotagePlanInteract';
import { ensureMeasureLayer, updateMeasureLayer } from './measureLayer';
import { ensureSavedMeasuresLayer, updateSavedMeasuresLayer } from './savedMeasuresLayer';
import {
  ensureWeatherLayer,
  updateWeatherLayer,
  ensurePinnedWindLayer,
  updatePinnedWindLayer,
  applyWindColourThresholds,
} from './weatherLayer';
import { ensureAisLayer, updateAisLayer, setAisLayersVisible, raiseAisLayers } from './aisLayer';
import { clipVectorToLand, type LngLat } from './aisVectorClip';
import { useAisStore } from '@/store/ais';
import { haptic } from '@/ui/haptics';
import { computeCpa } from '@/ais/cpa';
import { isLost, AGE_DROP_MS } from '@/ais/targetAge';
import {
  registerPmtiles,
  setRasterChart,
  setRasterCharts,
  setCellLayersHidden,
  setCellBaseFillsHidden,
} from './rasterLayer';
import { setTileBasemap, satelliteKey, buildTileUrl, resolveSatelliteSource, type ActiveTileBasemap } from './satelliteBasemap';
import { setVectorBasemap } from './vectorBasemap';
import { setFallbackBasemap, maptilerFallbackBasemap } from './fallbackBasemap';
import { isStyleReady } from './styleReady';
import { nextEpoch, shouldApplyStyleWork } from './styleEpoch';
import { useBootStore } from '@/store/boot';
import { maptilerGlyphsUrl } from '@/chart-sources/maptilerConfig';
import { useSatelliteStore } from '@/store/satellite';
import { setSeamarkOverlay, SEAMARK_ATTRIBUTION } from './seamarkOverlay';
import { nearestCachedPointFeature, NON_OBJECT_POINT_CLASSES } from './pickCachedFeature';
import { seamarkTilesTemplate, registerSeamarkCache } from './seamarkTileCache';
import { useSeamarkStore } from '@/store/seamark';
import { useRangeRingsStore } from '@/store/rangeRings';
import { useEblVrmStore } from '@/store/eblVrm';
import { useSearchIndexStore } from '@/store/searchIndex';
import { usePilotagePlannerStore, selectActivePlan } from '@/store/pilotagePlanner';
import { extractNamedPlaces } from '@/ui/shell/searchSources';
import { hydroSources } from '@/chart-sources/hydroProvider';
import { planResidency, preferredBand, type ResidencyCandidate, DEFAULT_MAX_CELLS } from './cellResidency';
import { traceResidency } from './residencyTrace';
import { readHeap } from '@/diag/heartbeat';
import { noteBootAttempt, markBootHealthy, HEALTHY_AFTER_MS } from '@/diag/safeStart';
import { useMeasureStore } from '@/store/measure';
import { useSavedMeasurementsStore } from '@/store/savedMeasurements';
import { ensureTssOverlay } from './tssOverlay';
import { registerPlotIcons, installPlotIconSupplier } from './icons';
import { installChartSymbols, setChartSymbolStyle } from './style/chartSymbols';
import { glyphStyleFor } from './style/symbolSets';
import { ensureLightSectorsLayer } from './lightSectors';
import { getPalette } from './style/palettes';
import { useSafetyStore } from '@/store/safety';
import { useMobSearchStore } from '@/store/mobSearch';
import { useContourHighlightStore } from '@/store/contourHighlight';
import { useEnvStore } from '@/store/environment';
import { streamArrowsAt, streamAtPosition } from '@/tides';
import { windFieldAt, windAt } from '@/weather';
import { planAutoRoute, detectPilotageSections } from '@/passage';
import { dangersAhead, recommendXtdForLeg, dangersWithin, chartedDepthUnderBoat, chartedDepthDetailAt, depthUnderKeel, scanRouteHazards, ChartIndex, swingRadiusNm, dragCheck, outsideZone, projectGrounding, evaluateRouteAlarms, lookaheadDistanceNm, legBBox } from '@/safety';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { edgeMidpoint } from '@/safety/anchorZone';

/**
 * Snapshot the anchor-watch layer input from the store (plus whether the drag
 * alarm is standing), so the on-chart hook, boundary and swing track always match
 * what the alarm is checking. Read via getState, so it works from both the map's
 * load closures and the per-fix effect.
 */
function currentAnchorWatchInput(dragging: boolean): AnchorWatchLayerInput {
  const s = useAnchorWatchStore.getState();
  return {
    anchor: s.anchor,
    mode: s.mode,
    radiusNm: swingRadiusNm({ scopeM: s.scopeM, depthM: s.depthM, bowHeightM: s.bowHeightM }),
    zone: s.zone,
    swingTrack: s.swingTrack,
    dragging,
  };
}
import { heightAt, nearestHeightPort } from '@/tides/heights';
import { heightNowM } from '@/tides/tideNow';
import { useTideSourceStore } from '@/store/tideSources';
import { useAlarmsStore, type AlarmsLive } from '@/store/alarms';
import { useSettingsStore } from '@/store/settings';
import { usePositionSourceStore } from '@/store/positionSource';
import { useOwnVectorsStore } from '@/store/ownVectors';
import { usePilotageStore, nextTransitId, nextHeadingId } from '@/store/pilotage';
import { useGnssStore } from '@/store/gnss';
import {
  clearingDragUpdate,
  isClearingTripped,
  clearingDisplayName,
  isClearingDepthTripped,
  clearingDepthDisplayName,
  transitExtendDragUpdate,
  headingDefaultFrom,
} from '@/pilotage';
import { legCrossTrackNm, checkRouteSafety, summariseRouteSafety, legHighlightSeverity } from '@/routing';
import { sampleTide } from '@/environment';
import type { ChartFeatureCollection } from '@/types';
import { zocBandFromCatzoc } from '@/chart';

export interface MapViewProps {
  initialCenter?: [number, number];
  initialZoom?: number;
  instanceId?: string;
}

const DEFAULT_CENTER: [number, number] = [-5.9, 46.05];
const DEFAULT_ZOOM = 9;

// Close-quarters danger cutoff: any live AIS target whose predicted CPA is under
// this always draws red, regardless of the (user-configurable, toggleable) CPA
// alarm. Under half a mile is a genuine near-miss worth flagging on the chart on
// its own, so the colour doesn't depend on how the alarm happens to be set.
const AIS_DANGER_CPA_NM = 0.5;

// Persist the last map centre/zoom so a kill/restart reopens where we were
// looking, not back at the default training-area view.
const MAPVIEW_LS = 'acp.mapView.v1';

/**
 * How long the app has to stay behind before we let the chart's memory go.
 *
 * Long enough that checking the almanac and coming straight back costs nothing,
 * short enough that it has happened well before a phone starts hunting for
 * memory to reclaim.
 */
const RELEASE_CHART_AFTER_MS = 20_000;
interface SavedMapView {
  center: [number, number];
  zoom: number;
}
function loadMapView(fallbackCentre: [number, number], fallbackZoom: number): SavedMapView {
  const v = loadJson<SavedMapView>(MAPVIEW_LS, { center: fallbackCentre, zoom: fallbackZoom });
  const c = v.center;
  // Guard a corrupt/partial store so a bad value can't leave a blank map.
  const ok =
    Array.isArray(c) &&
    c.length === 2 &&
    Number.isFinite(c[0]) &&
    Number.isFinite(c[1]) &&
    Math.abs(c[1]) <= 90 &&
    Number.isFinite(v.zoom);
  return ok ? v : { center: fallbackCentre, zoom: fallbackZoom };
}
function saveMapView(map: maplibregl.Map): void {
  const c = map.getCenter();
  saveJson(MAPVIEW_LS, { center: [c.lng, c.lat], zoom: map.getZoom() });
}

/** Ray-casting point-in-ring test on a [lon, lat] ring. */
function pointInRing(pt: [number, number], ring: number[][]): boolean {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring and outside any holes. */
function polygonContains(rings: number[][][], pt: [number, number]): boolean {
  if (!rings.length || !pointInRing(pt, rings[0])) return false;
  for (let i = 1; i < rings.length; i++) if (pointInRing(pt, rings[i])) return false;
  return true;
}

/** Merge the visible saved tracks (drawn faded, as 'secondary') with the live
 * tracks, for the ownship layer. Saved tracks are kept in their own store. */
function withSavedTracks(
  live: Record<string, Track>,
  saved: { id: string; name: string; points: TrackPoint[] }[],
  hiddenIds: string[],
): Record<string, Track> {
  const merged: Record<string, Track> = { ...live };
  for (const st of saved) {
    if (hiddenIds.includes(st.id)) continue;
    merged[`saved-${st.id}`] = { id: `saved-${st.id}`, kind: 'secondary', points: st.points };
  }
  return merged;
}

/** Whether the position on screen should not be presented as a live fix, read
 *  fresh from the store for the mount-time ensure calls that run outside the
 *  reactive selector. Covers both a fix that has gone stale and the phone's
 *  cached starting position, which is not a fix of ours at all. See fixReliable. */
function deviceFixStale(): boolean {
  const s = usePositionSourceStore.getState();
  return fixUnreliable({ sourceKind: s.sourceKind, fixHealth: s.fixHealth });
}

/** A short label for a charted feature (its name, else its S-57 class). */
function featureLabelOf(feat: ChartFeature): string {
  const attrs = (feat.properties?.attrs ?? {}) as Record<string, unknown>;
  if (typeof attrs.name === 'string' && attrs.name) return attrs.name;
  return String(feat.properties?.objClass ?? 'Object');
}

/**
 * The ZOC band covering a [lon, lat], by a point-in-polygon over the M_QUAL
 * (quality-of-data) polygons held in the cell cache. Every charted feature
 * already carries its area's `zoc` stamp from the converter, so a tap that lands
 * on an object or area reads it for free; this lookup is the fallback for the
 * rare tap that lands on a gap with no feature under it. The set is small (a few
 * hundred polygons) and only walked on a tap, so it's cheap.
 */
function zocBandAt(cells: Iterable<ChartFeatureCollection>, pt: [number, number]): string | null {
  for (const fc of cells) {
    for (const ft of fc.features) {
      if (ft.properties.objClass !== 'M_QUAL') continue;
      const raw = (ft.properties.attrs?.raw ?? {}) as Record<string, unknown>;
      const band = zocBandFromCatzoc(raw.CATZOC);
      if (!band) continue;
      const g = ft.geometry;
      if (g.type === 'Polygon' && polygonContains(g.coordinates as number[][][], pt)) return band;
      if (
        g.type === 'MultiPolygon' &&
        (g.coordinates as number[][][][]).some((poly) => polygonContains(poly, pt))
      )
        return band;
    }
  }
  return null;
}

export function MapView({
  initialCenter = DEFAULT_CENTER,
  initialZoom = DEFAULT_ZOOM,
  instanceId = 'main',
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  // Monotonic style GENERATION, bumped on every map.setStyle (see styleEpoch.ts).
  // Async re-apply paths capture the generation they belong to and bail if a newer
  // setStyle supersedes them, so stale work never mutates the freshly-swapped style.
  const styleEpochRef = useRef(0);
  const cellsLoadedRef = useRef<Set<string>>(new Set());

  const [cells, setCells] = useState<CellMeta[]>([]);
  const palette = useChartPlotterStore((s) => s.palette);
  const symbolSet = useChartPlotterStore((s) => s.symbolSet);
  const rotation = useChartPlotterStore((s) => s.rotation);
  const orientNonce = useChartPlotterStore((s) => s.orientNonce);
  const orientation = useChartPlotterStore((s) => s.orientation);
  const tracking = useShellStore((s) => s.tracking);
  const categories = useChartPlotterStore((s) => s.displayCategories);
  const declutterLevel = useChartPlotterStore((s) => s.declutterLevel);
  const groupVisibility = useChartPlotterStore((s) => s.groupVisibility);
  const setSelectedFeature = useChartPlotterStore((s) => s.setSelectedFeature);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const tracks = useChartPlotterStore((s) => s.tracks);
  const savedTracks = useSavedTracksStore((s) => s.tracks);
  const savedHiddenIds = useSavedTracksStore((s) => s.hiddenIds);
  const setCursor = useChartPlotterStore((s) => s.setCursor);
  const activeTool = useChartPlotterStore((s) => s.activeTool);
  const objectsById = useObjectsStore((s) => s.byId);
  const objectsOrder = useObjectsStore((s) => s.order);
  const routes = useRoutingStore((s) => s.routes);
  const activeRouteId = useRoutingStore((s) => s.activeRouteId);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const marks = useRoutingStore((s) => s.marks);
  const notes = useNotesStore((s) => s.notes);
  const goto = useRoutingStore((s) => s.goto);
  const destination = useRoutingStore((s) => s.destination);
  const plannedRouteId = useRoutePlanStore((s) => s.plannedRouteId);
  const autoRequest = useRoutePlanStore((s) => s.autoRequest);
  const focus = useShellStore((s) => s.focus);
  const fit = useShellStore((s) => s.fit);
  const hiddenRouteIds = useRoutingStore((s) => s.hiddenRouteIds);
  const followFromLegIndex = useRoutingStore((s) => s.followFromLegIndex);
  const activeLegIndex = useRoutingStore((s) => s.activeLegIndex);
  const clearings = usePilotageStore((s) => s.clearings);
  const activeClearingId = usePilotageStore((s) => s.activeClearingId);
  const hiddenPlanIds = usePilotageStore((s) => s.hiddenPlanIds);
  const transits = usePilotageStore((s) => s.transits);
  const activeTransitId = usePilotageStore((s) => s.activeTransitId);
  const headingBearings = usePilotageStore((s) => s.headingBearings);
  const activeHeadingId = usePilotageStore((s) => s.activeHeadingId);
  const clearingDepths = usePilotageStore((s) => s.clearingDepths);
  // Visible (not hidden by plan) clearing-depth values, for the highlight layer.
  // Reads the store directly so the map handlers get the current set.
  const visibleClearingDepthValues = () => {
    const ps = usePilotageStore.getState();
    return ps.clearingDepths
      .filter((d) => d.planId == null || !ps.hiddenPlanIds.includes(d.planId))
      .map((d) => d.valueM);
  };
  // Depth-contour values the user has asked to emphasise (display-only halo).
  const highlightedContourValues = useContourHighlightStore((s) => s.values);
  // Last "Check route" result. A stable object reference (only changes on a new
  // run or a clear), so this plain selector can't drive a re-render loop.
  const routeSafetyCheck = useRouteSafetyStore((s) => s.check);
  const alarmClearings = useAlarmsStore((s) => s.live.clearing);
  const editingLegXtd = useShellStore((s) => s.editingLegXtd);
  const anchor = useSafetyStore((s) => s.anchor);
  const mob = useSafetyStore((s) => s.mob);
  const mobSearchOverlay = useMobSearchStore((s) => s.overlay);
  const safetySettings = useSafetyStore((s) => s.settings);
  const dangers = useSafetyStore((s) => s.dangers);
  const setDangers = useSafetyStore((s) => s.setDangers);
  // Anchor-watch redraw triggers: the hook, its boundary mode, the drawn zone and
  // the swing inputs. Deliberately NOT swingTrack (that grows every fix; the trail
  // is redrawn from the per-fix alarm effect instead of re-rendering the map).
  const awAnchor = useAnchorWatchStore((s) => s.anchor);
  const awMode = useAnchorWatchStore((s) => s.mode);
  const awZone = useAnchorWatchStore((s) => s.zone);
  const awScopeM = useAnchorWatchStore((s) => s.scopeM);
  const awDepthM = useAnchorWatchStore((s) => s.depthM);
  const awBowHeightM = useAnchorWatchStore((s) => s.bowHeightM);
  const alarmsConfig = useAlarmsStore((s) => s.config);
  const setAlarmsLive = useAlarmsStore((s) => s.setLive);
  // Boat-profile figures (single source of truth, from the Settings sheet).
  const boatDraughtM = useSettingsStore((s) => s.draughtM);
  const safetyDepthM = useSettingsStore((s) => s.safetyDepthM);
  const cruiseSpeedKn = useSettingsStore((s) => s.cruiseSpeedKn);
  const shadingShallowM = useSettingsStore((s) => s.shadingShallowM);
  const shadingDeepM = useSettingsStore((s) => s.shadingDeepM);
  const depthShadingMode = useSettingsStore((s) => s.depthShadingMode);
  const ownVectorMin = useSettingsStore((s) => s.ownVectorMin);
  const ownVectorColour = useSettingsStore((s) => s.ownVectorColour);
  const areaSelectArmed = useChartPacksStore((s) => s.areaSelectArmed);
  const windGreenFromKn = useSettingsStore((s) => s.windGreenFromKn);
  const windRedFromKn = useSettingsStore((s) => s.windRedFromKn);
  // Own-ship vector toggles + the position source drive which vectors draw. The
  // toggles are read reactively so flipping one repaints straight away; the wind
  // and tide samples are taken at the boat from the same feeds the overlays use.
  const vCogsog = useOwnVectorsStore((s) => s.cogsog);
  const vHeading = useOwnVectorsStore((s) => s.heading);
  const vWind = useOwnVectorsStore((s) => s.wind);
  const vTide = useOwnVectorsStore((s) => s.tide);
  const positionSourceKind = usePositionSourceStore((s) => s.sourceKind);
  // Built from the live stores (hoisted, so the mount handlers above can call it
  // too). Heading only draws when the simulator supplies one; wind only when a
  // wind source is there; tide only where there's stream data. This keeps the
  // renderer honest: a toggle can be on while its source is missing, and then we
  // simply hand over nothing to draw and the menu explains why.
  /**
   * Ground resolution at the boat, metres per screen pixel. Web mercator, so it
   * depends on latitude as well as zoom: at 56 N a metre is a good deal more
   * pixels than at the equator. This is what decides whether the hull is worth
   * drawing or the symbol reads better.
   */
  function metresPerPixelNow(): number | undefined {
    const map = mapRef.current;
    if (!map) return undefined;
    const lat = map.getCenter().lat;
    return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, map.getZoom());
  }

  function currentOwnVectors(): OwnVectorRender {
    const t = useOwnVectorsStore.getState();
    const kind = usePositionSourceStore.getState().sourceKind;
    const pos = useChartPlotterStore.getState().ownship.position;
    const ms = useEnvStore.getState().streamTimeMs;
    const wsrc = useEnvStore.getState().weatherSource;
    return {
      cogsog: t.cogsog,
      heading: t.heading && kind === 'sim',
      // Whether a heading source EXISTS, which is not the same as whether the
      // user wants the vector drawn. The boat symbol needs the former.
      hasHeadingSource: kind === 'sim',
      boatIcon:
        ({ triangle: 'plot-boat', sail: 'plot-boat-sail', motor: 'plot-boat-motor', power: 'plot-boat-power' } as Record<string, string>)[
          useSettingsStore.getState().ownshipIcon
        ] ?? 'plot-boat-ardent',
      hull: hullSettings(),
      metresPerPixel: metresPerPixelNow(),
      wind: t.wind,
      tide: t.tide,
      windSample: t.wind && kind === 'sim' && pos ? windAt(wsrc, ms, pos) : null,
      tideSample: t.tide && pos ? streamAtPosition(pos, ms) : null,
    };
  }
  // Real device fix gone stale/lost -> grey the boat. Null (sim) reads as false,
  // so the training/sim position path is never dimmed.
  const fixStale = usePositionSourceStore((s) =>
    fixUnreliable({ sourceKind: s.sourceKind, fixHealth: s.fixHealth }),
  );
  const cellsRef = useRef<Map<string, ChartFeatureCollection>>(new Map());
  // Every cell the manifests offer, as residency candidates. Listing them is
  // cheap (bounds and scale band); loading them is not, so only the ones the
  // plan asks for are fetched. See src/map/cellResidency.ts.
  const cellCandidatesRef = useRef<ResidencyCandidate[]>([]);
  const syncCellsRef = useRef<(() => Promise<void>) | null>(null);
  const refreshCellsRef = useRef<(() => Promise<unknown>) | null>(null);
  // Named places accumulate as cells load, so the Search / Go To box does not
  // forget a harbour the moment you pan away from it.
  const placesRef = useRef<Map<string, ReturnType<typeof extractNamedPlaces>[number]>>(new Map());
  // The boat moves several times a second in the sim; re-planning residency that
  // often would scan every candidate cell for nothing. Only re-check once it has
  // actually made ground.
  const lastShipSyncRef = useRef<[number, number] | null>(null);
  // Bumped every time the resident cell set changes. The index used to be keyed
  // on feature count alone, which was fine when cells only ever accumulated. Now
  // that cells come and go, two different sets can hold the same number of
  // features, and a stale index feeding the depth alarm is exactly the kind of
  // quiet wrong answer we cannot have.
  const cellsGenRef = useRef(0);
  // A chart spatial index, rebuilt only when the resident cells change, so
  // the per-tick alarms reuse it instead of scanning every feature each time.
  const chartIndexRef = useRef<{ index: ChartIndex; size: number; gen: number } | null>(null);
  const chartIndexNow = (): ChartIndex => {
    const cells = Array.from(cellsRef.current.values());
    const size = cells.reduce((n, c) => n + c.features.length, 0);
    const gen = cellsGenRef.current;
    const cached = chartIndexRef.current;
    if (cached && cached.size === size && cached.gen === gen) return cached.index;
    const built = { index: new ChartIndex(cells), size, gen };
    chartIndexRef.current = built;
    return built.index;
  };
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const streamSource = useEnvStore((s) => s.streamSource);
  const overlayMode = useEnvStore((s) => s.overlayMode);
  const weatherSource = useEnvStore((s) => s.weatherSource);
  const pinnedWind = useEnvStore((s) => s.pinnedWind);
  const weatherForecastH = useEnvStore((s) => s.weatherForecastH);
  const heightPortId = useEnvStore((s) => s.heightPortId);
  const streamOn = useChartPlotterStore((s) => s.groupVisibility['tidal-stream'] ?? true);
  // The tidal-stream overlay shows whenever its Overlays toggle is on and streams are
  // the active overlay, in ANY mode, so toggling it (e.g. on the training charts in Live)
  // actually draws the arrows. The time scrubber stays a Plotting tool.
  const showStream = streamOn && overlayMode === 'streams';
  // Same reason as loadRealWeatherForView below: the overlay is for under way,
  // not only for planning.
  const showWeather = overlayMode === 'weather';
  const windStyle = useWeatherOverlayStore((s) => s.windStyle);
  // Which variable the coloured wash is showing. The arrows always show the wind,
  // because direction is what a symbol is for; the wash is whatever was picked.
  const overlayVariableId = useWeatherOverlayStore((s) => s.variableId);
  const overlayWaveModelId = useWeatherOverlayStore((s) => s.waveModelId);
  const wxField = useWeatherFieldStore((s) => s.field);
  const wxFieldVarId = useWeatherFieldStore((s) => s.variableId);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const satelliteApiKey = useSatelliteStore((s) => s.apiKey);
  const imageryDim = useSatelliteStore((s) => s.dim);
  const satEnabled = useSatelliteStore((s) => s.enabled);
  const satSourceId = useSatelliteStore((s) => s.sourceId);
  const satOpacity = useSatelliteStore((s) => s.opacity);
  const seamarkOn = useSeamarkStore((s) => s.enabled);
  const rrEnabled = useRangeRingsStore((s) => s.enabled);
  const rrInterval = useRangeRingsStore((s) => s.intervalNm);
  const rrCount = useRangeRingsStore((s) => s.count);
  const rrSpokes = useRangeRingsStore((s) => s.spokes);
  const rrAuto = useRangeRingsStore((s) => s.auto);
  const eblOn = useEblVrmStore((s) => s.eblOn);
  const piOn = useEblVrmStore((s) => s.piOn);
  const piBearingDeg = useEblVrmStore((s) => s.piBearingDeg);
  const piOffsetNm = useEblVrmStore((s) => s.piOffsetNm);
  const eblBearingDeg = useEblVrmStore((s) => s.eblBearingDeg);
  const vrmOn = useEblVrmStore((s) => s.vrmOn);
  const vrmRangeNm = useEblVrmStore((s) => s.vrmRangeNm);
  const planPlans = usePilotagePlannerStore((s) => s.plans);
  const planActiveId = usePilotagePlannerStore((s) => s.activePlanId);
  const planVisible = usePilotagePlannerStore((s) => s.planVisible);
  const planBuilding = usePilotagePlannerStore((s) => s.building);
  const measurePoints = useMeasureStore((s) => s.points);
  const savedMeasures = useSavedMeasurementsStore((s) => s.items);
  const bsbCharts = useChartSourcesStore((s) => s.bsbCharts);
  const packCharts = useChartSourcesStore((s) => s.packCharts);
  const realWxSamples = useRealWeatherStore((s) => s.samples);
  // Real-world wind wherever the chart shows real water; the fictional training
  // charts stay synthetic. Was pinned to the Antares 'bsb' provider, which left the
  // real NOAA cells drawing a made-up wind field over real US water.
  const useRealWx = weatherSource !== 'synthetic' && providerHasRealWeather(chartProviderId);
  // Built only when the overlay is actually shown, and memoised so they stay
  // STABLE references between 1 Hz position ticks. Before, these were rebuilt as a
  // fresh array every render, which re-ran the overlay effect below every second
  // even with both overlays OFF (the Live-navigation case).
  const weatherSamples = useMemo(
    () => (showWeather ? (useRealWx ? realWxSamples : windFieldAt('synthetic', streamTimeMs)) : []),
    [showWeather, useRealWx, realWxSamples, streamTimeMs],
  );
  const streamArrows = useMemo(
    () => (showStream ? streamArrowsAt(streamSource, streamTimeMs) : []),
    [showStream, streamSource, streamTimeMs],
  );
  const chartOverrideId = useChartPlotterStore((s) => s.chartOverrideId);

  const visibility: LayerVisibilityState = {
    categories,
    groups: { ...defaultGroupVisibility(), ...groupVisibility },
    declutterLevel,
  };

  // Cells now load long after mount, so the loader reads these rather than the
  // values captured when the map was created. Otherwise a cell that arrives
  // after a palette swap gets stamped with the old colours.
  const paletteRef = useRef(palette);
  paletteRef.current = palette;
  const symbolSetRef = useRef(symbolSet);
  symbolSetRef.current = symbolSet;
  const visibilityRef = useRef(visibility);
  visibilityRef.current = visibility;

  // Keep chart under the boat as it moves, so the depth and danger alarms never
  // run against water we have not loaded. Throttled by distance rather than
  // time: 0.01 degrees is about half a mile, well inside the six-mile radius the
  // plan keeps around the boat.
  const shipLon = ownship.position?.[0];
  const shipLat = ownship.position?.[1];
  useEffect(() => {
    if (shipLon === undefined || shipLat === undefined) return;
    const prev = lastShipSyncRef.current;
    if (prev && Math.abs(prev[0] - shipLon) < 0.01 && Math.abs(prev[1] - shipLat) < 0.01) return;
    lastShipSyncRef.current = [shipLon, shipLat];
    void syncCellsRef.current?.();
  }, [shipLon, shipLat]);

  // And along the route, so the hazard scan sees the whole passage rather than
  // the part of it that happens to be on screen.
  useEffect(() => {
    void syncCellsRef.current?.();
  }, [routes, activeRouteId, followRouteId, plannedRouteId]);

  // Switching a vector pack on or off changes what cells exist at all, so the
  // candidate list is rebuilt rather than just re-planned.
  const packCellSources = useChartSourcesStore((s) => s.packCellSources);
  useEffect(() => {
    void refreshCellsRef.current?.();
  }, [packCellSources]);

  // Read the PlotObject graph as an ordered array, straight from the store, so
  // the mount and palette-swap handlers always get the current set.
  const currentGraphObjects = () => {
    const s = useObjectsStore.getState();
    return s.order.map((id) => s.byId[id]).filter(Boolean);
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    registerPmtiles();
    // Native-only: route seamark tiles through the on-disk cache protocol.
    registerSeamarkCache();

    // The crash-loop breaker. If the last two launches died before they could
    // be called healthy, the saved view is the prime suspect (it is the one
    // piece of state that decides how much work a start does), so we come back
    // at the home position instead. Recorded BEFORE the map is built, so a
    // launch that dies building it leaves its mark. See diag/safeStart.ts.
    const unsafeStart = noteBootAttempt();
    const savedView = unsafeStart
      ? { center: initialCenter, zoom: initialZoom }
      : loadMapView(initialCenter, initialZoom);
    if (unsafeStart) {
      traceResidency('SAFE START: two launches died young, ignoring the saved view');
      // Said out loud, because a chart that silently moves home is its own
      // small mystery, and the skipper should know the app rescued itself.
      window.setTimeout(() => {
        useShellStore
          .getState()
          .showToast('The last start failed, so we have come back to the home view.');
      }, 3500);
    }
    // Survive long enough and the count resets, so ordinary use never trips it.
    const healthyTimer = window.setTimeout(markBootHealthy, HEALTHY_AFTER_MS);
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildBaseStyle(palette),
      center: savedView.center,
      zoom: savedView.zoom,
      bearing: rotation,
      pitchWithRotate: false,
      dragRotate: true,
      attributionControl: false,
      // Keeping the WebGL buffer readable after each frame lets the feedback tool
      // read the chart canvas back for a screenshot. On mobile the drawing buffer is
      // cleared after each frame unless we hold it, so map.getCanvas().toDataURL()
      // came back black there and the bug-report screenshots were unusable. Holding
      // it on every device costs a little GPU memory on a phone (accepted) but means
      // the feedback capture always reads the real chart (see captureScreenshot).
      preserveDrawingBuffer: true,
      // Cap the tile cache on a phone. MapLibre otherwise keeps a generous ring
      // of off-screen tiles ready (great on desktop, but each one is retained
      // geometry/GPU memory). On a memory-tight phone that retained ring is the
      // headroom the charts need when the position load and boat icon land, so we
      // hold only a couple of screenfuls and let the rest re-tile if we pan back.
      // Desktop keeps the default (undefined = MapLibre's own sizing).
      maxTileCacheSize: isMobileDevice() ? 24 : undefined,
    });

    // No on-screen zoom buttons: they sat under the top-right data field, and a
    // touch-first plotter zooms by pinch (wheel on desktop). The scale bar stays.
    map.addControl(
      new maplibregl.ScaleControl({ maxWidth: 120, unit: 'nautical' }),
      'bottom-left',
    );

    // Let bolt-ons outside the React tree (the feedback screenshot) reach the map.
    setActiveMap(map);
    // The open-water "What's here?" popup lives outside this tree but wants the
    // charted depth at an arbitrary tapped point. Register a query over the live
    // cell index; it stays honest (null) when the source carries no depth.
    setChartedDepthQuery((pos) => {
      if (!providerHasDepthData(useChartPlotterStore.getState().chartProviderId)) return null;
      return chartedDepthUnderBoat(pos, chartIndexNow());
    });
    // The same point, in parts: the depth area and the nearest sounding. The
    // readout uses this rather than the single cautious figure above, because a
    // depth area's shallow end printed on its own reads as a depth we haven't got.
    setChartedDepthDetailQuery((pos) => {
      if (!providerHasDepthData(useChartPlotterStore.getState().chartProviderId)) return null;
      return chartedDepthDetailAt(pos, chartIndexNow());
    });
    // Same for the charted quality band (CATZOC/ZOC), read from the M_QUAL
    // polygons in the live cells. Quality lives in the vector ENC, so it stays
    // honest (null) on a raster/tile source that carries none.
    setChartedZocQuery((pos) => {
      if (!providerHasDepthData(useChartPlotterStore.getState().chartProviderId)) return null;
      return zocBandAt(cellsRef.current.values(), pos);
    });
    // Let the Route sheet's "Check route" run its geometry against the live chart
    // without reaching into this component: publish a probe over the SAME land
    // source (LNDARE from the chart index) and the SAME charted-depth sampler the
    // grounding alarm uses. Kept honest — depth is null when the source carries
    // none, so the checker flags it as a caution rather than a false all-clear.
    setChartProbe({
      landRingsForLeg: (from, to) => {
        const idx = chartIndexNow();
        const rings: LngLat[][] = [];
        for (const feat of idx.search(legBBox(from, to, 0.05))) {
          const isLand = feat.properties.group === 'land' || feat.properties.objClass === 'LNDARE';
          if (!isLand) continue;
          const g = feat.geometry;
          if (g.type === 'Polygon') {
            rings.push(g.coordinates[0] as LngLat[]);
          } else if (g.type === 'MultiPolygon') {
            for (const poly of g.coordinates) rings.push(poly[0] as LngLat[]);
          }
        }
        return rings;
      },
      depthAt: (pos) => {
        if (!providerHasDepthData(useChartPlotterStore.getState().chartProviderId)) return null;
        return chartedDepthUnderBoat(pos, chartIndexNow(), 0.04);
      },
      // Coverage: is there any charted feature loaded around this point? A point
      // with nothing near it is outside the charts we hold, so we report it as
      // such rather than judging it safe.
      inCoverage: (pos) => chartIndexNow().search(legBBox(pos, pos, 0.5)).length > 0,
      // Height of tide above datum at a spot and time, from the nearest training
      // port's curve, so the route check can add the tide on top of the charted
      // depth. Null (not zero) where there's no depth chart, or where the nearest
      // port is too far to speak honestly for this spot, so the check asks for a
      // manual tide calc instead of reading a distant port.
      tideHeightAt: (pos, ms) => {
        if (!providerHasDepthData(useChartPlotterStore.getState().chartProviderId)) return null;
        const near = nearestHeightPort(pos);
        if (!near || near.distNm > 60) return null;
        return heightAt(near.port, ms);
      },
    });

    // WebGL context loss recovery. On a memory-tight phone the GPU can drop the
    // context (the charts flash up, then go white). MapLibre re-creates the
    // painter and re-uploads the sources on restore, but only if the browser is
    // allowed to restore, which needs preventDefault on the lost event. We add
    // that belt-and-braces and force a redraw once it's back, so a hiccup recovers
    // instead of leaving a permanently white chart.
    const canvasEl = map.getCanvas();
    const onContextLost = (e: Event) => {
      e.preventDefault();
      // Written down as well as logged. A phone has no console, so a warning here
      // was only ever readable by somebody with the handset plugged into a laptop,
      // and a lost context looks exactly like a chart that failed to draw.
      recordContext('context-lost', containerRef.current, canvasEl);
      console.warn('[chart] WebGL context lost; waiting for the browser to restore it');
    };
    const onContextRestored = () => {
      recordContext('context-restored', containerRef.current, canvasEl);
      console.info('[chart] WebGL context restored; redrawing');
      // MapLibre re-creates the painter and re-uploads sources/tiles on restore,
      // but the images we add ourselves (the boat icon and the S-52 buoy glyphs)
      // are the one class of resource a stricter driver can drop with the old
      // context. Re-register them (both idempotent) so the boat and buoyage come
      // back rather than leaving a chart that redraws blank where a symbol was.
      try {
        const ui = getPalette(useChartPlotterStore.getState().palette);
        registerPlotIcons(map, ui.UI_ACCENT, ui.CHBLK);
        installChartSymbols(map);
      } catch (err) {
        console.warn('[chart] re-registering symbols after context restore failed', err);
      }
      map.triggerRepaint();
    };
    canvasEl.addEventListener('webglcontextlost', onContextLost, false);
    canvasEl.addEventListener('webglcontextrestored', onContextRestored, false);

    // Supply the S-52 buoyage symbol images on demand (survives palette swaps).
    installChartSymbols(map);
    // Supply the plot overlay icons (boat, marks, fixes) on demand too, so a dropped
    // image (context loss / eviction / race) is redrawn the instant a symbol needs it.
    installPlotIconSupplier(map);

    const onMove = (e: MapMouseEvent) => {
      setCursor([e.lngLat.lng, e.lngLat.lat]);
    };
    map.on('mousemove', onMove);
    // Re-evaluate the auto-selected raster chart whenever the view settles, and
    // publish the visible extent so the chart-source UI can tell whether the
    // selected provider has a chart on screen.
    const publishViewBounds = () => {
      const b = map.getBounds();
      useChartSourcesStore
        .getState()
        .setViewBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    };
    // Residency waits for the pan to stop. moveend fires per gesture segment,
    // so a long drag across an ocean fired a plan (and its loads) several times
    // on the way, which is chart being fetched and tiled for water already
    // behind us. Settling first means one honest plan for where we ACTUALLY
    // stopped. Short enough that a deliberate pan-and-look feels immediate.
    const RESIDENCY_SETTLE_MS = 350;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const syncCellsWhenSettled = () => {
      if (settleTimer !== null) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        settleTimer = null;
        void syncCellsRef.current?.();
      }, RESIDENCY_SETTLE_MS);
    };
    const onMoveEnd = () => {
      publishViewBounds();
      saveMapView(map);
      // Imperative, React-free: the raster auto-pick (raster providers only) and a
      // real-weather refetch for the new extent. No setState, so a pan/zoom never
      // re-renders this component.
      applyChartSource('move');
      loadRealWeatherForView();
      // Pull in the cells the new extent needs and drop the ones it does not,
      // once the pan has actually settled.
      syncCellsWhenSettled();
    };
    map.on('moveend', onMoveEnd);
    // Returning from a background freeze: everything time- or fetch-shaped is
    // stale. Resize first (the canvas watcher usually catches size, but it is
    // free), then re-pick the chart, re-pull the cells, refetch the weather.
    const onThawed = () => {
      try {
        map.resize();
        // A freeze that lands mid-touch can leave MapLibre's gesture handlers
        // believing a gesture is still in progress, and a map that ignores
        // every touch afterwards reads as frozen while the pulses are perfect
        // (fb-msnbeubc-1, forty seconds after a 59-minute thaw). Toggling the
        // handlers resets their internal state; the area select keeps its
        // deliberate pan-off.
        if (!useChartPacksStore.getState().areaSelectArmed) {
          map.dragPan.disable();
          map.dragPan.enable();
        }
        map.touchZoomRotate.disable();
        map.touchZoomRotate.enable();
        map.scrollZoom.disable();
        map.scrollZoom.enable();
      } catch {
        /* mid-teardown */
      }
      // Staggered, not all at once. The renderer is at its most fragile in
      // the seconds after a thaw (Android reclaimed it 85 s after one on
      // 10 Aug, fb-msncmroz-0), and firing the chart re-pick, the cell sync
      // and a weather refetch in the same breath is a memory spike at the
      // worst possible moment. Spreading them costs three seconds of
      // staleness on data that is already half an hour stale.
      window.setTimeout(() => applyChartSource('move'), 500);
      window.setTimeout(() => void syncCellsRef.current?.(), 1500);
      window.setTimeout(() => loadRealWeatherForView(), 3000);
    };
    window.addEventListener('acp:thawed', onThawed);
    publishViewBounds();
    // A manual pan/drag disengages the tracking lock so the user can look around.
    const onUserPan = () => {
      if (useShellStore.getState().tracking) useShellStore.getState().setTracking(false);
    };
    map.on('dragstart', onUserPan);
    // Keep the boat centred after a user zoom while the tracking lock is on.
    const onZoomEnd = () => {
      saveMapView(map);
      if (!useShellStore.getState().tracking) return;
      const os = useChartPlotterStore.getState().ownship.position;
      if (os) map.easeTo({ center: os, duration: 300 });
    };
    map.on('zoomend', onZoomEnd);

    // --- Long-press to drop a waypoint -------------------------------------
    // Desktop fires `contextmenu` on right-click, but a touch long-press does
    // not reliably fire it on mobile browsers, so on a phone the gesture did
    // nothing. We add an explicit touch long-press detector: hold a single
    // finger still for LONG_PRESS_MS and we drop a waypoint there. A real drag
    // (a pan) past a few pixels cancels it.
    const LONG_PRESS_MS = 450;
    const LONG_PRESS_MOVE_PX = 12;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let longPressStartPx: { x: number; y: number } | null = null;
    // True for the moment just after a long-press fires, so the trailing click
    // (the finger lifting) doesn't also open the chart-tap sheet.
    let longPressFired = false;
    const clearLongPress = () => {
      if (longPressTimer !== null) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      longPressStartPx = null;
    };

    // Which route's leg line is under this screen point, if any? Used to decide
    // whether a long-press inserts mid-leg or appends to the end.
    const routeLineAt = (point: { x: number; y: number }): string | null => {
      if (!map.getLayer('route-line')) return null;
      const pad = 8;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['route-line'] },
      );
      const f = hits[0];
      return f?.properties?.routeId ? String(f.properties.routeId) : null;
    };

    // The magenta "course from the boat" line under the point: the go-to line to a
    // mark, or a followed route's active leg. Both are anchored at own ship and drawn
    // magenta, and both used to be untappable and unlabelled ("what's the purple
    // line?"), so this lets a tap say what it is.
    const courseLineAt = (
      point: { x: number; y: number },
    ): { kind: 'goto' | 'active-leg'; name: string } | null => {
      const layers: string[] = [];
      if (map.getLayer('marks-goto-leg')) layers.push('marks-goto-leg');
      if (map.getLayer('route-active-leg')) layers.push('route-active-leg');
      if (!layers.length) return null;
      const pad = 8;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers },
      );
      const f = hits[0];
      if (!f) return null;
      const name = f.properties?.name ? String(f.properties.name) : '';
      return f.properties?.kind === 'active-leg'
        ? { kind: 'active-leg', name }
        : { kind: 'goto', name };
    };

    // Returns the just-inserted route waypoint when a long-press lands on a route
    // leg, so the touch handler can hand straight into a drag without lifting the
    // finger. Every other outcome returns undefined.
    const dropWaypoint = (
      lnglat: [number, number],
      point?: { x: number; y: number },
    ): { routeId: string; wpId: string } | undefined => {
      if (usePilotagePlannerStore.getState().building) return;
      // Just tapped "New route": the on-screen instruction asks for a long-press
      // to drop the first waypoint. Honour that here and start the route, rather
      // than letting the press fall through to the open-water radial ring (the
      // clash the removal of the old routing mode left behind). One-shot: the
      // drop clears the flag, so normal long-press behaviour resumes after.
      if (useRoutingStore.getState().awaitingRouteStart) {
        const rstate = useRoutingStore.getState();
        rstate.dropRouteWaypoint(lnglat);
        rstate.setAwaitingRouteStart(false);
        useShellStore.getState().showToast('Route started — long-press to add more waypoints');
        return;
      }
      // A long-press ON the ownship boat opens the track-actions popover
      // (stop / reset / save the live recording track), not a
      // waypoint. Detected by pixel proximity to the projected boat, the same
      // threshold the vessel tap uses, so it works at any zoom.
      if (point) {
        const os = useChartPlotterStore.getState().ownship.position;
        if (os) {
          const osPx = map.project(os);
          if (Math.hypot(osPx.x - point.x, osPx.y - point.y) <= 24) {
            useShellStore.getState().openTrackMenu(point.x, point.y);
            return;
          }
        }
      }
      // Long-press ON a chart object opens that object, not the open-water
      // ring, so the hold gesture reads the same everywhere: "tell me about /
      // let me act on the thing under my finger". Most-specific target wins.
      if (point) {
        // AIS target -> its detail card (name, CPA/TCPA, vessel info). A plain
        // tap already does this; the long-press just has to stop falling
        // through to the open-water ring the way it used to.
        if (map.getLayer('ais-tri')) {
          const aisHit = map.queryRenderedFeatures(
            [
              [point.x - 8, point.y - 8],
              [point.x + 8, point.y + 8],
            ],
            { layers: ['ais-tri'] },
          );
          const mmsi = aisHit[0]?.properties?.mmsi;
          if (mmsi != null) {
            useAisStore.getState().select(Number(mmsi));
            useShellStore.getState().openSheet('ais');
            return;
          }
        }
        // EBL / VRM line -> the EBL/VRM settings sheet (swing the bearing, size
        // the ring), rather than dropping a waypoint on top of it.
        const eblVrmLayers = ['ebl-vrm-ebl', 'ebl-vrm-vrm', 'ebl-vrm-label'].filter((l) =>
          map.getLayer(l),
        );
        if (eblVrmLayers.length) {
          const hit = map.queryRenderedFeatures(
            [
              [point.x - 8, point.y - 8],
              [point.x + 8, point.y + 8],
            ],
            { layers: eblVrmLayers },
          );
          if (hit.length) {
            useShellStore.getState().openSheet('eblvrm');
            return;
          }
        }
        // Range ring / spoke -> the range-rings settings (interval, count,
        // spokes), reached through the overlays menu leaf.
        const ringLayers = ['range-rings-ring', 'range-rings-spoke'].filter((l) => map.getLayer(l));
        if (ringLayers.length) {
          const hit = map.queryRenderedFeatures(
            [
              [point.x - 6, point.y - 6],
              [point.x + 6, point.y + 6],
            ],
            { layers: ringLayers },
          );
          if (hit.length) {
            useShellStore.getState().openMenuAt('layers', 'range-rings');
            return;
          }
        }
      }
      const rs = useRoutingStore.getState();
      // Open-water long-press opens the radial action ring (set destination, drop
      // mark, anchor, range/bearing, route from here, wind here). Route building
      // and a press on an existing route leg fall through to the waypoint logic
      // below, so the ring never gets in the way of editing a route.
      if (point && !rs.buildingRouteId && !routeLineAt(point)) {
        // Adaptive ring: on a charted object open the object ring seeded with
        // it (details, plot transit, clearing bearing, set destination…);
        // otherwise the open-water ring. Same charted-object pick as a tap.
        const ringObj = ringObjectAt(point);
        useShellStore.getState().openRing(lnglat, point.x, point.y, ringObj);
        return;
      }
      // Routing isn't gated behind a mode any more — the tap ring's "Route" and the
      // chart sheet's "Start route" both drop a waypoint here whatever mode we're in.
      // So a long-press just carries on with the route logic below (insert on a leg,
      // append while building, else offer the choice) rather than telling us to
      // switch to a route-plotting mode that no longer exists.
      // On a route leg: insert at that point, splitting the leg in two.
      if (point) {
        const routeId = routeLineAt(point);
        const route = routeId ? rs.routes.find((r) => r.id === routeId) : undefined;
        if (routeId && route) {
          const at = nearestInsertIndex(route.waypoints.map((w) => w.position), lnglat);
          if (at !== null) {
            const wpId = nextWaypointId();
            rs.insertWaypoint(routeId, at, {
              id: wpId,
              position: lnglat,
              name: `WP${route.waypoints.length + 1}`,
            });
            rs.setActiveRoute(routeId);
            useShellStore.getState().showToast('Waypoint inserted — drag to position, lift to drop');
            return { routeId, wpId };
          }
        }
      }
      // Open water while building: append to the route. Otherwise offer the
      // choice (single mark vs start a route) via the chart sheet.
      if (rs.buildingRouteId) {
        rs.dropRouteWaypoint(lnglat);
        useShellStore.getState().showToast('Waypoint dropped');
        return;
      }
      useShellStore.getState().setChartTap(lnglat, zocBandAt(cellsRef.current.values(), lnglat));
      useShellStore.getState().openSheet('chart');
    };

    // Is there a route waypoint under this screen point? Lets waypoint taps and
    // drags take precedence over the long-press and chart-tap behaviour.
    const waypointAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('route-waypoints')) return undefined;
      const pad = 10;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['route-waypoints'] },
      );
      return hits[0];
    };

    const markAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('marks-icon')) return undefined;
      const pad = 10;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['marks-icon'] },
      );
      return hits[0];
    };

    // Is the MOB last-known mark under this screen point? A press picks it up so
    // the casualty position can be dragged to a better estimate; a plain tap opens
    // the drift & search sheet. Padded like the waypoint/mark hit test so the small
    // lifebuoy icon is as easy to grab.
    const mobMarkAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('safety-mob')) return undefined;
      const pad = 12;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['safety-mob'] },
      );
      return hits[0];
    };

    // Is there a chart note (post-it) under this screen point? A tap on one opens
    // it to read/edit/delete, so it's caught before the open-water chart tap.
    const noteAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('notes-icon')) return undefined;
      const pad = 10;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['notes-icon'] },
      );
      return hits[0];
    };

    // Lights (and any LIGHTS sectors) at a tapped feature's position, so the
    // info sheet can show every sector of a sectored light or lighthouse.
    const lightSiblingsFor = (pick: ChartFeature): ChartFeature[] => {
      const oc = pick.properties?.objClass;
      if ((oc !== 'LIGHTS' && oc !== 'LNDMRK') || pick.geometry?.type !== 'Point') return [];
      const [plon, plat] = pick.geometry.coordinates as [number, number];
      const out: ChartFeature[] = [];
      for (const fc of cellsRef.current.values()) {
        for (const ft of fc.features) {
          if (ft.properties.objClass !== 'LIGHTS' || ft.geometry.type !== 'Point') continue;
          const [lon, lat] = ft.geometry.coordinates as [number, number];
          if (Math.abs(lon - plon) < 3e-4 && Math.abs(lat - plat) < 3e-4) out.push(ft as ChartFeature);
        }
      }
      return out;
    };

    // The charted object under a long-press, or null on open water. Uses the
    // same pick as a tap: the rendered cell features (falling back to the
    // cached ENC points when the vector layers are hidden under a raster base),
    // filtered to real point objects (buoys, beacons, lights, landmarks…) so a
    // depth area or sounding under the finger still opens the open-water ring.
    const ringObjectAt = (point: { x: number; y: number }): RingObject | null => {
      const layerIds =
        map
          .getStyle()
          .layers?.map((l) => l.id)
          .filter((id) => id.startsWith('cell-')) ?? [];
      const pad = 8;
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [point.x - pad, point.y - pad],
        [point.x + pad, point.y + pad],
      ];
      const feats = layerIds.length ? map.queryRenderedFeatures(bbox, { layers: layerIds }) : [];
      let pick = feats.find((f) => {
        const oc = f.properties?.objClass;
        return (
          f.geometry?.type === 'Point' &&
          !(typeof oc === 'string' && NON_OBJECT_POINT_CLASSES.has(oc))
        );
      }) as unknown as ChartFeature | undefined;
      if (
        !pick &&
        layerIds.length &&
        layerIds.every((id) => map.getLayoutProperty(id, 'visibility') === 'none')
      ) {
        pick = nearestCachedPointFeature(
          cellsRef.current.values(),
          (c) => map.project(c),
          point,
          SNAP_RADIUS_PX,
        );
      }
      if (!pick) return null;
      const pos = featurePoint(pick.geometry);
      if (!pos) return null;
      return {
        position: pos,
        label: featureTitle(pick),
        objClass: pick.properties?.objClass ?? null,
        feature: pick,
        siblings: lightSiblingsFor(pick),
      };
    };

    // A touch anywhere on ANY leg's corridor (band or handles) is a drag target.
    // Grabbing it both enters edit mode for that leg and starts the drag, so the
    // corridor is directly draggable wherever it's drawn.
    const xtdBandAt = (point: { x: number; y: number }) => {
      const layers: string[] = [];
      if (map.getLayer('route-xtd-handle')) layers.push('route-xtd-handle');
      if (map.getLayer('route-xtd-band')) layers.push('route-xtd-band');
      if (!layers.length) return undefined;
      const pad = 14;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers },
      );
      return hits.find(
        (h) => h.properties?.routeId && (h.properties.kind === 'xtd-handle' || h.properties.kind === 'xtd-band'),
      );
    };

    // A waypoint/mark proximity ring under the point, so the ring edge can be
    // grabbed and dragged to set the alarm radius (same idea as the XTD corridor).
    const ringAt = (point: { x: number; y: number }) => {
      const layers: string[] = [];
      if (map.getLayer('route-wp-ring')) layers.push('route-wp-ring');
      if (map.getLayer('marks-ring')) layers.push('marks-ring');
      if (!layers.length) return undefined;
      const pad = 14;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers },
      );
      return hits.find((h) => h.properties?.cLon != null && h.properties?.cLat != null);
    };

    // Keep EBL/VRM grabs clear of the boat itself, so the track-menu long-press
    // and vessel tap still win right on top of own ship (both lines start there).
    const OWNSHIP_GRAB_GUARD_PX = 28;
    // The EBL bearing line under the point, when the EBL is on: grabbing it swings
    // the bearing. Returns the boat centre to swing about, or undefined.
    const eblLineAt = (point: { x: number; y: number }): [number, number] | undefined => {
      if (!useEblVrmStore.getState().eblOn || !map.getLayer('ebl-vrm-ebl')) return undefined;
      const os = useChartPlotterStore.getState().ownship.position;
      if (!os) return undefined;
      const osPx = map.project(os);
      if (Math.hypot(osPx.x - point.x, osPx.y - point.y) < OWNSHIP_GRAB_GUARD_PX) return undefined;
      const pad = 8;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['ebl-vrm-ebl'] },
      );
      return hits.length ? os : undefined;
    };
    // The VRM range circle edge under the point, when the VRM is on: grabbing it
    // sizes the range. Returns the boat centre to size from, or undefined.
    const vrmRingAt = (point: { x: number; y: number }): [number, number] | undefined => {
      if (!useEblVrmStore.getState().vrmOn || !map.getLayer('ebl-vrm-vrm')) return undefined;
      const os = useChartPlotterStore.getState().ownship.position;
      if (!os) return undefined;
      const pad = 10;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['ebl-vrm-vrm'] },
      );
      return hits.length ? os : undefined;
    };
    // The PI line under the point, when it's on: grabbing it slides the offset,
    // the same gesture as sizing the VRM, because the offset IS the passing
    // distance and sliding the line to the mark is how it's set in practice.
    const piLineAt = (point: { x: number; y: number }): [number, number] | undefined => {
      if (!useEblVrmStore.getState().piOn || !map.getLayer('ebl-vrm-pi')) return undefined;
      const os = useChartPlotterStore.getState().ownship.position;
      if (!os) return undefined;
      const osPx = map.project(os);
      if (Math.hypot(osPx.x - point.x, osPx.y - point.y) < OWNSHIP_GRAB_GUARD_PX) return undefined;
      const pad = 8;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['ebl-vrm-pi'] },
      );
      return hits.length ? os : undefined;
    };

    // Snap a tap to a nearby charted POINT object (buoy, beacon, light, landmark,
    // structure...), so a transit mark can lock onto its exact charted position.
    // It reads the underlying VECTOR cell cache directly, not the rendered
    // layers, so it still snaps when a raster chart is displayed (vector layers
    // hidden); with nothing close it falls back to the raw tapped lat/long, so
    // headlands and arbitrary points work too.
    const SNAP_RADIUS_PX = 18;
    const SNAP_SKIP = new Set(['SOUNDG', 'M_QUAL', 'M_COVR', 'DEPARE', 'DEPCNT', 'SEAARE']);
    const snapPoint = (
      point: { x: number; y: number },
      lnglat: [number, number],
    ): { position: [number, number]; label: string; snapped: boolean } => {
      let best: { position: [number, number]; label: string } | null = null;
      let bestD = SNAP_RADIUS_PX;
      for (const fc of cellsRef.current.values()) {
        for (const ft of fc.features) {
          const g = ft.geometry;
          if (!g || g.type !== 'Point') continue;
          const oc = ft.properties?.objClass;
          if (typeof oc === 'string' && SNAP_SKIP.has(oc)) continue;
          const c = g.coordinates as [number, number];
          const xy = map.project(c);
          const d = Math.hypot(xy.x - point.x, xy.y - point.y);
          if (d < bestD) {
            bestD = d;
            best = { position: c, label: featureLabelOf(ft) };
          }
        }
      }
      return best ? { ...best, snapped: true } : { position: lnglat, label: 'Point', snapped: false };
    };

    const transitHandleAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('pilotage-transit-handle')) return undefined;
      const pad = 12;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['pilotage-transit-handle'] },
      );
      return hits[0];
    };

    const headingHandleAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('pilotage-heading-handle')) return undefined;
      const pad = 12;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['pilotage-heading-handle'] },
      );
      return hits[0];
    };

    const onClick = (e: MapMouseEvent) => {
      // Any deliberate tap clears a pinned wind barb ("stays till we click
      // elsewhere"). It's set from the long-press ring, so this tap is never
      // the one that created it.
      if (useEnvStore.getState().pinnedWind) useEnvStore.getState().clearPinnedWind();
      // Plan-building owns chart taps, ahead of the long-press swallow below, so a
      // deliberate/lingering tap still places a plan element rather than being
      // eaten as a long-press.
      if (usePilotagePlannerStore.getState().building) {
        longPressFired = false;
        handlePlanClick(map, e.point, [e.lngLat.lng, e.lngLat.lat]);
        return;
      }
      // Swallow the click that follows a long-press, so a dropped waypoint
      // doesn't also open the chart-tap sheet.
      if (longPressFired) {
        longPressFired = false;
        return;
      }
      // Anchor-zone drawing: while the skipper is laying down a custom swing zone,
      // each chart tap drops a polygon vertex and does nothing else.
      if (useAnchorWatchStore.getState().drawingZone) {
        useAnchorWatchStore.getState().addZonePoint([e.lngLat.lng, e.lngLat.lat]);
        return;
      }
      // A waypoint, mark or XTD-handle tap is handled by the drag/edit handlers,
      // so don't also treat it as a chart tap.
      if (waypointAt(e.point) || markAt(e.point) || xtdBandAt(e.point) || ringAt(e.point)) return;
      // A tap on the EBL/VRM is handled by the drag/tap-to-edit path (it opens the
      // EBL/VRM sheet on a no-move press), so don't also treat it as a chart tap.
      if (eblLineAt(e.point) || vrmRingAt(e.point)) return;
      // An AIS target tap selects it and opens its detail, ahead of the chart tap.
      if (map.getLayer('ais-tri')) {
        const aisHit = map.queryRenderedFeatures(
          [
            [e.point.x - 7, e.point.y - 7],
            [e.point.x + 7, e.point.y + 7],
          ],
          { layers: ['ais-tri'] },
        );
        const hitMmsi = aisHit[0]?.properties?.mmsi;
        if (hitMmsi != null) {
          useAisStore.getState().select(Number(hitMmsi));
          useShellStore.getState().openSheet('ais');
          return;
        }
      }
      const tool = useChartPlotterStore.getState().activeTool;
      const lnglat: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      // Place-anchor mode: the next tap says where the anchor already is. The
      // tapped point is used as-is, with no offset to the bow, because the
      // skipper is pointing at the anchor rather than at the boat. Takes
      // precedence over the ordinary tap behaviour, and is one-shot.
      if (useShellStore.getState().anchorPickArmed) {
        const already = useAnchorWatchStore.getState().anchor != null;
        haptic('commit');
        useAnchorWatchStore.getState().dropAnchor(lnglat);
        useShellStore.getState().cancelAnchorPick();
        useShellStore
          .getState()
          .showToast(already ? 'Anchor moved there' : 'Anchor placed there — watch armed');
        return;
      }

      // Place-note mode: the next tap drops a new post-it here and opens its
      // editor. Entered from the notes hub's "New note", where we haven't got a
      // location yet, so this is how we choose WHERE the note goes. One-shot.
      if (useShellStore.getState().noteArmed) {
        const id = nextNoteId();
        const now = Date.now();
        useNotesStore
          .getState()
          .addNote({ id, position: lnglat, text: '', createdMs: now, updatedMs: now });
        useShellStore.getState().cancelPlaceNote();
        useShellStore.getState().setEditingNote(id);
        useShellStore.getState().openSheet('note');
        return;
      }

      // Fix builder: when armed, the next tap captures a mark for an
      // observation, naming it from the charted feature under the tap if there
      // is one. Takes precedence over the normal tap behaviour.
      if (useShellStore.getState().fixArmed) {
        let label = 'Mark';
        const cellLayerIds =
          map.getStyle().layers?.map((l) => l.id).filter((id) => id.startsWith('cell-')) ?? [];
        if (cellLayerIds.length) {
          const feats = map.queryRenderedFeatures(
            [
              [e.point.x - 6, e.point.y - 6],
              [e.point.x + 6, e.point.y + 6],
            ],
            { layers: cellLayerIds },
          );
          const objClass = feats[0]?.properties?.objClass;
          if (typeof objClass === 'string' && objClass) label = objClass;
        }
        useShellStore.getState().setPendingMark({ position: lnglat, label });
        return;
      }

      // Transit placement: tap anywhere to set the BACK (rear) mark, then the
      // FRONT mark. Each tap snaps to a nearby charted object when close to one,
      // else uses the raw tapped position. Works over raster charts too.
      {
        const shTr = useShellStore.getState();
        if (shTr.transitArmed && !shTr.pendingTransit) {
          const snap = snapPoint(e.point, lnglat);
          useShellStore.getState().armTransitPick({ position: snap.position, label: snap.label });
          useShellStore
            .getState()
            .showToast(
              snap.snapped
                ? `Back mark snapped to ${snap.label} — now tap the front mark`
                : 'Back mark set — now tap the front mark',
            );
          return;
        }
        if (shTr.pendingTransit) {
          const pend = shTr.pendingTransit;
          const snap = snapPoint(e.point, lnglat);
          usePilotageStore.getState().addTransit({
            id: nextTransitId(),
            a: pend.position,
            b: snap.position,
            aLabel: pend.label,
            bLabel: snap.label,
            planId: null,
          });
          useShellStore.getState().clearPendingTransit();
          useShellStore.getState().openSheet('transit');
          useShellStore
            .getState()
            .showToast(snap.snapped ? `Front mark snapped to ${snap.label} — transit plotted` : 'Transit plotted');
          return;
        }
      }

      // Heading / back bearing: a single tap drops the bearing on the tapped
      // point (snapping to a nearby charted object). The vessel-side end starts
      // at own ship, or a short default line when there's no fix, ready to drag.
      {
        const sh = useShellStore.getState();
        if (sh.headingArmed) {
          const kind = sh.headingArmed;
          const snap = snapPoint(e.point, lnglat);
          const os = useChartPlotterStore.getState().ownship.position;
          usePilotageStore.getState().addHeadingBearing({
            id: nextHeadingId(),
            kind,
            anchor: snap.position,
            anchorLabel: snap.label,
            from: os ?? headingDefaultFrom(snap.position),
            planId: null,
          });
          sh.clearHeadingArm();
          useShellStore.getState().openSheet('heading');
          useShellStore
            .getState()
            .showToast(
              snap.snapped
                ? `${kind === 'back' ? 'Back' : 'Heading'} bearing on ${snap.label} — drag the vessel end`
                : `${kind === 'back' ? 'Back' : 'Heading'} bearing set — drag the vessel end`,
            );
          return;
        }
      }

      if (tool === 'place_ownship') {
        useChartPlotterStore.getState().setOwnship({ position: lnglat });
        useChartPlotterStore.getState().setActiveTool('pan');
        return;
      }

      if (tool === 'dr') {
        // DR now builds a proper graph construction (start, water track, DR),
        // so it preserves its inputs and renders through the object layer.
        const os = useChartPlotterStore.getState().ownship;
        const built = buildDR({
          start: lnglat,
          headingDeg: os.heading || 90,
          speedKnots: os.stw || os.sog || cruiseSpeedKn,
          hours: 1,
          time: { iso: new Date().toISOString() },
          newId: nextObjectId,
        });
        useObjectsStore.getState().putObjects([built.start, built.waterTrack, built.dr]);
        return;
      }

      if (tool === 'ep') {
        // EP builds the full construction: start, water track, DR, tidal vector,
        // EP, and ground track. Tide comes from the active scenario; with none
        // loaded set/rate are zero, so the EP simply equals the DR.
        const os = useChartPlotterStore.getState().ownship;
        const env = useEnvStore.getState();
        const stream = sampleTide(env.tide, env.clockH);
        const built = buildEP({
          start: lnglat,
          headingDeg: os.heading || 90,
          speedKnots: os.stw || os.sog || cruiseSpeedKn,
          hours: 1,
          tidalSet: stream.setDeg,
          tidalRate: stream.rateKn,
          time: { iso: new Date().toISOString() },
          newId: nextObjectId,
        });
        useObjectsStore
          .getState()
          .putObjects([
            built.start,
            built.waterTrack,
            built.dr,
            built.tidalVector,
            built.ep,
            built.groundTrack,
            built.ellipse,
          ]);
        return;
      }

      if (tool === 'vrm') {
        // VRM is a range circle from the boat, modelled as a range_arc on the
        // graph so it renders through the same object layer as everything else.
        const centre = useChartPlotterStore.getState().ownship.position ?? lnglat;
        const rangeNm = distanceBetween(centre, lnglat);
        useObjectsStore.getState().putObjects([
          {
            id: nextObjectId('vrm'),
            kind: 'range_arc',
            geometry: { type: 'range', centre, rangeNm },
            label: `${rangeNm.toFixed(2)} nm`,
            source: { method: 'manual' },
            parents: [],
            children: [],
            userPlaced: true,
          },
        ]);
        return;
      }

      if (tool === 'ebl') {
        // EBL is a bearing line from the boat, modelled as a bearing_lop.
        const centre = useChartPlotterStore.getState().ownship.position ?? lnglat;
        const bearingDeg = bearingBetween(centre, lnglat);
        useObjectsStore.getState().putObjects([
          {
            id: nextObjectId('ebl'),
            kind: 'bearing_lop',
            geometry: { type: 'bearing', from: centre, bearingDeg },
            label: `${Math.round(bearingDeg)}°`,
            source: { method: 'manual' },
            parents: [],
            children: [],
            userPlaced: true,
          },
        ]);
        return;
      }

      if (tool === 'measure') {
        // A tap on an existing vertex is a grab-to-drag (handled on mousedown),
        // not a new point, so don't stack a duplicate on top of it.
        if (measureVertexAt(e.point)) return;
        // Lay a ruler point. The ruler reads distance/bearing per leg and the
        // running total (MeasureReadout); Done clears it. Started from the menu
        // (empty) or the long-press radial (seeded at the press point).
        useMeasureStore.getState().addPoint(lnglat);
        return;
      }

      if (tool === 'add_waypoint') {
        useRoutingStore.getState().addWaypoint(null, {
          id: nextWaypointId(),
          position: lnglat,
          name: `WP${(useRoutingStore.getState().routes[0]?.waypoints.length ?? 0) + 1}`,
        });
        return;
      }

      // A tap on an existing note (post-it) opens it to read, edit or delete. It
      // sits above the chart, so we catch it before the charted-feature query
      // and the open-water chart tap below.
      {
        const nHit = noteAt(e.point);
        const nid = nHit?.properties?.noteId;
        if (nid != null) {
          useShellStore.getState().setEditingNote(String(nid));
          useShellStore.getState().openSheet('note');
          return;
        }
      }

      const resting = tool === 'pan' || tool === 'none';

      // Tap an edge handle on the anchor boundary: split that edge, putting a
      // new point where the handle was, ready to drag. Same idea as adding a
      // waypoint to a route leg, and it comes before everything else because
      // the boundary sits over the swing track and often over the boat.
      if (resting) {
        const midF = anchorZoneMidpointAt(e.point);
        if (midF?.properties) {
          const i = Number(midF.properties.index);
          const zone = useAnchorWatchStore.getState().zone;
          // Where the HANDLE was, which is on the curve, not halfway along the
          // straight line between the two points. Splitting to the chord
          // midpoint would jump the boundary inwards the moment it was tapped.
          const at = edgeMidpoint(zone, i);
          if (at) {
            haptic('commit');
            useAnchorWatchStore.getState().insertZonePoint(i, at);
            useShellStore.getState().showToast('Point added — drag it to shape the boundary');
          }
          return;
        }
      }

      // Tap a saved measurement (its line / circle / label): offer Edit or
      // Delete. It used to delete on the spot, so a mis-tap near a pinned ruler
      // destroyed it with no confirm and no undo (fb-msbzhxuj-2). Deleting work
      // should take a decision, not a fat finger.
      if (resting && map.getLayer('saved-measure-line')) {
        const hits = map.queryRenderedFeatures(
          [
            [e.point.x - 6, e.point.y - 6],
            [e.point.x + 6, e.point.y + 6],
          ],
          { layers: ['saved-measure-line', 'saved-measure-label', 'saved-measure-circle'] },
        );
        const mid = hits[0]?.properties?.mid;
        if (mid != null) {
          const id = String(mid);
          const saved = useSavedMeasurementsStore.getState().items.find((m) => m.id === id);
          useShellStore.getState().showConfirm({
            message: saved ? `Measurement, ${measurementLabel(saved.points)}.` : 'This measurement.',
            confirmLabel: 'Delete',
            onConfirm: () => {
              useSavedMeasurementsStore.getState().remove(id);
              useShellStore.getState().showToast('Measurement removed');
            },
            altLabel: 'Edit',
            onAlt: () => {
              // Editing lifts it back onto the live ruler, points and all, and
              // takes it off the saved list: one ruler, in one place, rather
              // than a copy quietly shadowing the pinned original.
              if (!saved) return;
              useMeasureStore.getState().setPoints(saved.points);
              useSavedMeasurementsStore.getState().remove(id);
              useChartPlotterStore.getState().setActiveTool('measure');
              useShellStore.getState().showToast('Measurement reopened for editing');
            },
          });
          return;
        }
      }

      // Tap the vessel: open the vessel sheet. Detected by pixel proximity of
      // the click to the projected ownship position, so it works whatever the
      // zoom. Only at rest, so a plotting tap near the boat still plots.
      if (resting) {
        const os = useChartPlotterStore.getState().ownship.position;
        if (os) {
          const osPx = map.project(os);
          const dpx = Math.hypot(osPx.x - e.point.x, osPx.y - e.point.y);
          if (dpx <= 22) {
            useShellStore.getState().openSheet('vessel');
            return;
          }
        }
      }

      // Tap a route LINE (a waypoint tap is caught by the guard above and takes
      // precedence): select that route and open its info sheet, so a tap is
      // select-and-show, not a silent select. Resting only, so a plotting tap is
      // never hijacked.
      if (resting) {
        const rid = routeLineAt(e.point);
        if (rid) {
          const rs = useRoutingStore.getState();
          if (rs.activeRouteId !== rid) rs.setActiveRoute(rid);
          useShellStore.getState().openSheet('route');
          return;
        }
      }

      // Tap the magenta course line from the boat: say what it is. The go-to line
      // names its mark and offers to stop the go-to; the active-leg line points at
      // the next waypoint and opens the route it belongs to. Answers the "what's
      // the purple line?" question directly at the point of confusion.
      if (resting) {
        const course = courseLineAt(e.point);
        if (course) {
          if (course.kind === 'goto') {
            const where = course.name ? ` to ${course.name}` : '';
            useShellStore.getState().showToast(`Go-to line — your course${where}. Tap the mark to stop the go-to.`);
          } else {
            const where = course.name ? ` to ${course.name}` : '';
            useShellStore.getState().showToast(`Active leg — the course${where}, the waypoint you're making for.`);
            const fid = useRoutingStore.getState().followRouteId;
            if (fid) {
              useRoutingStore.getState().setActiveRoute(fid);
              useShellStore.getState().openSheet('route');
            }
          }
          return;
        }
      }

      // Tap a transit line (or its end marks) to open its info/editor.
      if (resting && map.getLayer('pilotage-transit-line')) {
        const hits = map.queryRenderedFeatures(
          [
            [e.point.x - 6, e.point.y - 6],
            [e.point.x + 6, e.point.y + 6],
          ],
          { layers: ['pilotage-transit-line', 'pilotage-transit-mark'] },
        );
        const tid = hits[0]?.properties?.id;
        if (tid != null) {
          usePilotageStore.getState().setActiveTransit(String(tid));
          useShellStore.getState().openSheet('transit');
          return;
        }
      }

      // Tap a heading / back bearing line (or its anchor) to open its editor.
      if (resting && map.getLayer('pilotage-heading-line')) {
        const hits = map.queryRenderedFeatures(
          [
            [e.point.x - 6, e.point.y - 6],
            [e.point.x + 6, e.point.y + 6],
          ],
          { layers: ['pilotage-heading-line', 'pilotage-heading-anchor'] },
        );
        const hid = hits[0]?.properties?.id;
        if (hid != null) {
          usePilotageStore.getState().setActiveHeadingBearing(String(hid));
          useShellStore.getState().openSheet('heading');
          return;
        }
      }

      // A tapped tidal diamond (stream arrow) opens its detail sheet, before the
      // charted-feature query so the diamond wins over anything drawn under it.
      if (map.getLayer('tide-diamond')) {
        const dPad = 8;
        const dBox: [maplibregl.PointLike, maplibregl.PointLike] = [
          [e.point.x - dPad, e.point.y - dPad],
          [e.point.x + dPad, e.point.y + dPad],
        ];
        const dHits = map.queryRenderedFeatures(dBox, { layers: ['tide-diamond'] });
        if (dHits.length) {
          setSelectedFeature(null);
          useShellStore.getState().setDiamondTap(lnglat);
          useShellStore.getState().openSheet('diamond');
          return;
        }
      }

      // Otherwise, query a charted feature first (works in every mode).
      const layerIds = map
        .getStyle()
        .layers?.map((l) => l.id)
        .filter((id) => id.startsWith(`cell-`)) ?? [];
      const padding = 6;
      const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
        [e.point.x - padding, e.point.y - padding],
        [e.point.x + padding, e.point.y + padding],
      ];
      const features = layerIds.length
        ? map.queryRenderedFeatures(bbox, { layers: layerIds })
        : [];
      let pick = features[0] as unknown as ChartFeature | undefined;
      // Raster / OpenSeaMap-tiles base: the vector cell layers are hidden (the base
      // IS the chart), so the rendered query finds nothing even where ENC data
      // covers the area, and a charted light returned no detail. When every cell
      // layer is hidden, fall back to the cached ENC features so tapping a light
      // (or other charted point object) still opens its detail. Gated on all-hidden
      // so the vector provider's tap radius is unchanged. OpenSeaMap's own seamark
      // tiles are raster PNGs with no attributes, so this reads the ENC cache under
      // them; with no ENC coverage there's nothing to query and we fall through.
      if (!pick && layerIds.length && layerIds.every((id) => map.getLayoutProperty(id, 'visibility') === 'none')) {
        pick = nearestCachedPointFeature(
          cellsRef.current.values(),
          (c) => map.project(c),
          e.point,
          SNAP_RADIUS_PX,
        );
      }
      if (pick) {
        setSelectedFeature(pick, lightSiblingsFor(pick));
        useShellStore.getState().openSheet('feature');
        return;
      }

      // Open water at rest: the chart-tap sheet (point, range and bearing from
      // the boat, and what we can do from there).
      setSelectedFeature(null);
      if (resting) {
        useShellStore.getState().setChartTap(lnglat, zocBandAt(cellsRef.current.values(), lnglat));
        useShellStore.getState().openSheet('chart');
      }
    };
    map.on('click', onClick);

    // Long-press / right-click drops a standalone waypoint, so there is no
    // waypoint button cluttering the resting screen, we put the action on the
    // chart itself.
    const onContextMenu = (e: MapMouseEvent) => {
      e.preventDefault();
      dropWaypoint([e.lngLat.lng, e.lngLat.lat], e.point);
    };
    map.on('contextmenu', onContextMenu);

    // Touch long-press (mobile): contextmenu is unreliable on touch, so detect
    // the hold ourselves and drop the same waypoint.
    const onTouchStart = (e: MapTouchEvent) => {
      // Single finger only; two fingers is a pinch/zoom, not a long-press.
      if (e.points.length !== 1) {
        clearLongPress();
        return;
      }
      // A touch on a draggable target (waypoint, mark, or a leg's XTD corridor)
      // is a drag/tap-to-edit, not a long-press. Grabbing a corridor also enters
      // edit mode for that leg.
      const dt = dragTargetAt(e.point);
      if (dt) {
        clearLongPress();
        e.preventDefault();
        // A hold on a boundary point offers to remove it. Deleting is a hold
        // rather than a tap because the same point is the thing you drag, and
        // a boundary that loses a point to a mis-tap is worse than one that
        // takes a moment to edit.
        if (dt.kind === 'anchorZone') {
          const idx = dt.index;
          const holdTimer = setTimeout(() => {
            useShellStore.getState().showConfirm({
              message: 'Remove this point from the anchor boundary?',
              confirmLabel: 'Remove',
              onConfirm: () => {
                const before = useAnchorWatchStore.getState().zone.length;
                useAnchorWatchStore.getState().removeZonePoint(idx);
                const after = useAnchorWatchStore.getState().zone.length;
                useShellStore
                  .getState()
                  .showToast(
                    after < before
                      ? 'Point removed'
                      : 'A boundary needs at least three points',
                  );
              },
            });
          }, LONG_PRESS_MS);
          map.once('touchend', () => clearTimeout(holdTimer));
          map.once('touchmove', () => clearTimeout(holdTimer));
        }
        if (dt.kind === 'xtd' && useShellStore.getState().mode !== 'Live') enterXtdEdit(dt.routeId, dt.fromId);
        beginDrag(dt, { x: e.point.x, y: e.point.y });
        // In Live, a waypoint/mark needs a deliberate long-press to pick up
        // before it moves; a plain touch there stays tap-to-info. This also
        // keeps the grab ahead of the open-water radial ring: the finger is on
        // the waypoint, so the ring never gets a look-in.
        if (
          useShellStore.getState().mode === 'Live' &&
          (dt.kind === 'route' || dt.kind === 'mark' || dt.kind === 'mob')
        ) {
          armLivePickup({ x: e.point.x, y: e.point.y });
        }
        map.on('touchmove', onWpDragMove);
        map.once('touchend', onWpDragEnd);
        // touchcancel too, and this line is a force-close's worth of hindsight
        // (fb-msmzm8r3-0): Android delivers cancel instead of end when a palm,
        // an edge gesture or the notification shade interrupts the touch, and a
        // drag that never ends leaves dragPan disabled, which reads as "the
        // app has frozen and won't let me pan" while every pulse is healthy.
        map.once('touchcancel', onWpDragEnd);
        return;
      }
      // While building a pilotage plan, a tap places a plan element (handled on
      // click). Don't arm the long-press, or a deliberate/lingering tap fires it
      // and its click gets swallowed, so nothing plots.
      if (usePilotagePlannerStore.getState().building) {
        clearLongPress();
        return;
      }
      clearLongPress();
      longPressFired = false;
      const px = { x: e.point.x, y: e.point.y };
      longPressStartPx = px;
      const lnglat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      longPressTimer = setTimeout(() => {
        longPressTimer = null;
        longPressFired = true;
        // One consistent buzz for every touch long-press, whatever it resolves
        // to (object detail, insert waypoint, track menu, or the open-water ring).
        haptic('tick');
        const inserted = dropWaypoint(lnglat, px);
        // Long-pressing a route leg drops a waypoint there; the haptic above is the
        // "it's added" cue. Keep the same finger down and this hands straight into a
        // drag of that new waypoint, so moving repositions it live and lifting drops
        // it. This long-press IS the deliberate pickup, so we arm livePickup and let
        // it move even in Live.
        if (inserted) {
          beginDrag({ kind: 'route', routeId: inserted.routeId, wpId: inserted.wpId }, px);
          livePickup = true;
          map.on('touchmove', onWpDragMove);
          map.once('touchend', onWpDragEnd);
          map.once('touchcancel', onWpDragEnd);
        }
      }, LONG_PRESS_MS);
    };
    const onTouchMove = (e: MapTouchEvent) => {
      if (longPressTimer === null || !longPressStartPx) return;
      const moved = Math.hypot(e.point.x - longPressStartPx.x, e.point.y - longPressStartPx.y);
      if (moved > LONG_PRESS_MOVE_PX) clearLongPress();
    };
    const onTouchEnd = () => clearLongPress();
    // Belt to the braces above: if pan is ever found disabled with no drag in
    // progress, whatever left it that way is over, so put the map back in the
    // user's hands rather than making them force-close to get it.
    const healPan = () => {
      try {
        // The area-select mode disables pan ON PURPOSE while armed; healing it
        // mid-select would make the map pan under the box being drawn.
        if (useChartPacksStore.getState().areaSelectArmed) return;
        if (!dragTarget && !map.dragPan.isEnabled()) map.dragPan.enable();
      } catch {
        /* a mid-teardown call is fine to ignore */
      }
    };
    map.on('touchstart', healPan);
    map.on('touchstart', onTouchStart);
    map.on('touchmove', onTouchMove);
    map.on('touchend', onTouchEnd);
    map.on('touchcancel', onTouchEnd);

    // --- Waypoint / mark / XTD-handle drag + tap-to-edit -------------------
    // Press a draggable target and drag to move it; a press that doesn't move
    // opens the editor (or, for an XTD handle, just sets the corridor). All of
    // it is driven off the global pointer events with a padded hit test, so
    // small standalone marks are as easy to hit as route waypoints.
    type RingRef = { kind: 'route'; routeId: string; wpId: string } | { kind: 'mark'; markId: string };
    type DragTarget =
      | { kind: 'route'; routeId: string; wpId: string }
      | { kind: 'mark'; markId: string }
      // The MOB last-known mark. It has no id (there's only ever one live MOB), so
      // the move handler reads the current mark from the safety store and rewrites
      // its position.
      | { kind: 'mob' }
      | { kind: 'xtd'; routeId: string; fromId: string; a: [number, number]; b: [number, number] }
      | { kind: 'ring'; ref: RingRef; centre: [number, number] }
      | { kind: 'clearing'; id: string }
      | { kind: 'transit'; id: string }
      | { kind: 'heading'; id: string }
      | { kind: 'measure'; index: number }
      // A point on the anchor boundary, dragged in or out to reshape it.
      | { kind: 'anchorZone'; index: number }
      // EBL bearing line and VRM range circle, both swung/sized about own ship.
      // `centre` is the boat at grab time; the move handler re-reads the live boat
      // so the drag stays anchored as the boat moves under it.
      | { kind: 'ebl'; centre: [number, number] }
      | { kind: 'vrm'; centre: [number, number] }
      | { kind: 'pi'; centre: [number, number] };
    let dragTarget: DragTarget | null = null;
    let dragMoved = false;
    let dragDownPx: { x: number; y: number } | null = null;
    let lastRingRadius = 0;
    const DRAG_THRESHOLD_PX = 6;

    // Live: a route waypoint or mark only moves after a deliberate long-press
    // picks it up, so an accidental nudge while we're following can never drag
    // the route about. In Plotting it moves on a plain press-drag as before, and
    // the ruler is exempt everywhere (a scratch tool, not a nav object).
    let livePickup = false;
    let pickupTimer: ReturnType<typeof setTimeout> | null = null;
    const clearPickup = () => {
      if (pickupTimer !== null) {
        clearTimeout(pickupTimer);
        pickupTimer = null;
      }
    };
    const armLivePickup = (px: { x: number; y: number }) => {
      clearPickup();
      pickupTimer = setTimeout(() => {
        pickupTimer = null;
        livePickup = true;
        // The hold itself isn't a move; re-anchor the jitter test to the press
        // point so the first small wobble after pickup doesn't count as a drag.
        dragMoved = false;
        dragDownPx = px;
        haptic('tick');
        useShellStore.getState().showToast('Waypoint picked up — drag to move, lift to drop');
      }, LONG_PRESS_MS);
    };

    const beginDrag = (target: DragTarget, downPx: { x: number; y: number }) => {
      dragTarget = target;
      dragMoved = false;
      dragDownPx = downPx;
      livePickup = false;
      clearPickup();
      // Hard-stop MapLibre's own pan so it can't grab the gesture mid-drag
      // (preventDefault alone is unreliable for this on touch).
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    };
    const onWpDragMove = (e: MapMouseEvent | MapTouchEvent) => {
      if (!dragTarget) return;
      // No editing in Live: a press on a waypoint there is tap-to-info only,
      // never a move, so following can't be turned into building/editing. The
      // ruler is the exception: it's a scratch measuring tool, not a persisted
      // nav object, so its points can be nudged in any mode.
      // In Live, only a picked-up waypoint/mark may move (see armLivePickup);
      // the ruler is exempt, being a scratch measuring tool, not a nav object.
      // The ruler and the EBL/VRM are scratch instruments, not persisted nav
      // objects, so they adjust on a plain drag in any mode; a route waypoint or
      // mark in Live still needs its deliberate pickup first.
      if (
        dragTarget.kind !== 'measure' &&
        dragTarget.kind !== 'anchorZone' &&
        dragTarget.kind !== 'ebl' &&
        dragTarget.kind !== 'vrm' &&
        dragTarget.kind !== 'pi' &&
        useShellStore.getState().mode === 'Live' &&
        !livePickup
      ) {
        // A finger that travels before the pickup fires isn't a deliberate
        // pickup, so cancel it; the press then falls back to tap-to-info on lift.
        if (dragDownPx) {
          const moved = Math.hypot(e.point.x - dragDownPx.x, e.point.y - dragDownPx.y);
          if (moved > DRAG_THRESHOLD_PX) clearPickup();
        }
        return;
      }
      // Ignore tap jitter: only a move past a few pixels counts as a drag, so a
      // tap opens the editor rather than nudging the marker a hair.
      if (!dragMoved && dragDownPx) {
        const moved = Math.hypot(e.point.x - dragDownPx.x, e.point.y - dragDownPx.y);
        if (moved < DRAG_THRESHOLD_PX) return;
      }
      dragMoved = true;
      const pos: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const rs = useRoutingStore.getState();
      if (dragTarget.kind === 'route') {
        rs.updateWaypoint(dragTarget.routeId, dragTarget.wpId, { position: pos });
      } else if (dragTarget.kind === 'mark') {
        rs.updateMark(dragTarget.markId, { position: pos });
      } else if (dragTarget.kind === 'anchorZone') {
        useAnchorWatchStore.getState().moveZonePoint(dragTarget.index, pos);
      } else if (dragTarget.kind === 'mob') {
        // Move the casualty's last-known position. The drift datum and search area
        // in the MOB sheet both derive from this, so they recompute from the new
        // point; keep the drop time so "adrift for" keeps counting from activation.
        const cur = useSafetyStore.getState().mob;
        if (cur) useSafetyStore.getState().setMob({ ...cur, position: pos });
      } else if (dragTarget.kind === 'ring') {
        // Radius = distance from the waypoint to where the ring edge is dragged.
        const r = Math.max(0.01, distanceBetween(dragTarget.centre, pos));
        lastRingRadius = r;
        if (dragTarget.ref.kind === 'route') {
          // Dragging the ring is an explicit "set this arrival radius", so arm the
          // alarm too — otherwise a waypoint switched off would stay silent.
          rs.updateWaypoint(dragTarget.ref.routeId, dragTarget.ref.wpId, { arrivalRadiusNm: r, arrivalAlarm: true });
        } else {
          rs.updateMark(dragTarget.ref.markId, { arrivalRadiusNm: r, arrivalAlarm: true });
        }
      } else if (dragTarget.kind === 'xtd') {
        const xtd = legCrossTrackNm(dragTarget.a, dragTarget.b, pos);
        rs.setLegXtd(dragTarget.routeId, dragTarget.fromId, Math.max(0.02, xtd));
      } else if (dragTarget.kind === 'clearing') {
        const clearingId = dragTarget.id;
        const ps = usePilotageStore.getState();
        const cb = ps.clearings.find((c) => c.id === clearingId);
        if (cb) {
          const u = clearingDragUpdate(cb.anchor, pos);
          ps.setClearingBearing(cb.id, u.bearingDeg, u.lengthNm);
        }
      } else if (dragTarget.kind === 'transit') {
        const transitId = dragTarget.id;
        const ps = usePilotageStore.getState();
        const t = ps.transits.find((x) => x.id === transitId);
        if (t) ps.updateTransit(t.id, { extendNm: transitExtendDragUpdate(t, pos) });
      } else if (dragTarget.kind === 'heading') {
        // Drag the vessel-side end; the bearing follows from from→object.
        usePilotageStore.getState().updateHeadingBearing(dragTarget.id, { from: pos });
      } else if (dragTarget.kind === 'measure') {
        // Move the ruler vertex live; the leg distance/bearing labels and the
        // running total redraw from the store on every move.
        useMeasureStore.getState().movePoint(dragTarget.index, pos);
      } else if (dragTarget.kind === 'ebl') {
        // Swing the EBL: its bearing is the direction from the boat to the finger.
        // Read the live boat so the line stays anchored if it moves under the drag;
        // the store wraps past north and the layer redraws with the new readout.
        const os = useChartPlotterStore.getState().ownship.position ?? dragTarget.centre;
        useEblVrmStore.getState().setEblBearingDeg(bearingBetween(os, pos));
      } else if (dragTarget.kind === 'vrm') {
        // Size the VRM: its range is the distance from the boat to the finger. The
        // store clamps to sensible bounds and the layer redraws with the new range.
        const os = useChartPlotterStore.getState().ownship.position ?? dragTarget.centre;
        useEblVrmStore.getState().setVrmRangeNm(distanceBetween(os, pos));
      } else if (dragTarget.kind === 'pi') {
        // Slide the PI: the offset is the finger's perpendicular distance from
        // the index bearing through the boat, signed so crossing the boat's
        // track flips the line to the other side in one gesture.
        const os = useChartPlotterStore.getState().ownship.position ?? dragTarget.centre;
        const brg = useEblVrmStore.getState().piBearingDeg;
        const d = distanceBetween(os, pos);
        const theta = ((bearingBetween(os, pos) - brg) * Math.PI) / 180;
        useEblVrmStore.getState().setPiOffsetNm(d * Math.sin(theta));
      }
    };
    const onWpDragEnd = () => {
      map.off('mousemove', onWpDragMove);
      map.off('touchmove', onWpDragMove);
      // Whichever of touchend / touchcancel / mouseup fired, take the others
      // down with it, or the survivor fires on some later unrelated gesture.
      map.off('touchend', onWpDragEnd);
      map.off('touchcancel', onWpDragEnd);
      map.off('mouseup', onWpDragEnd);
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      clearPickup();
      const wasLivePickup = livePickup;
      livePickup = false;
      const target = dragTarget;
      dragTarget = null;
      dragDownPx = null;
      if (target && !dragMoved && (target.kind === 'route' || target.kind === 'mark')) {
        const ref =
          target.kind === 'route'
            ? { type: 'route' as const, routeId: target.routeId, wpId: target.wpId }
            : { type: 'mark' as const, markId: target.markId };
        useShellStore.getState().setEditingWaypoint(ref);
        useShellStore.getState().openSheet('waypoint');
      } else if (target && !dragMoved && target.kind === 'mob') {
        // A tap that didn't move opens the drift & search sheet, the same
        // tap-to-open the marks and pilotage handles use.
        useShellStore.getState().openSheet('mobsearch');
      } else if (target && !dragMoved && target.kind === 'clearing') {
        usePilotageStore.getState().setActiveClearing(target.id);
        useShellStore.getState().openSheet('clearing');
      } else if (target && !dragMoved && target.kind === 'transit') {
        usePilotageStore.getState().setActiveTransit(target.id);
        useShellStore.getState().openSheet('transit');
      } else if (target && !dragMoved && target.kind === 'heading') {
        usePilotageStore.getState().setActiveHeadingBearing(target.id);
        useShellStore.getState().openSheet('heading');
      } else if (target && dragMoved && target.kind === 'ring') {
        useShellStore.getState().showToast(`Alarm radius ${lastRingRadius.toFixed(2)} nm`);
      } else if (target && !dragMoved && (target.kind === 'ebl' || target.kind === 'vrm' || target.kind === 'pi')) {
        // A press on the EBL/VRM that didn't move opens the sheet for fine setting,
        // the same tap-to-edit the waypoints and pilotage handles use.
        useShellStore.getState().openSheet('eblvrm');
      } else if (target && dragMoved && target.kind === 'ebl') {
        useShellStore.getState().showToast(`EBL ${formatBearing(useEblVrmStore.getState().eblBearingDeg)}`);
      } else if (target && dragMoved && target.kind === 'vrm') {
        useShellStore.getState().showToast(`VRM ${formatDistance(useEblVrmStore.getState().vrmRangeNm)}`);
      } else if (target && dragMoved && target.kind === 'pi') {
        const off = useEblVrmStore.getState().piOffsetNm;
        useShellStore.getState().showToast(`PI offset ${Math.abs(off).toFixed(2)} nm ${off >= 0 ? 'to starboard' : 'to port'}`);
      } else if (
        target &&
        dragMoved &&
        wasLivePickup &&
        (target.kind === 'route' || target.kind === 'mark')
      ) {
        // A waypoint we picked up and moved in Live: confirm the drop plainly.
        useShellStore.getState().showToast('Waypoint moved');
      }
    };

    const xtdLeg = (f: maplibregl.MapGeoJSONFeature): DragTarget | null => {
      const routeId = String(f.properties?.routeId ?? '');
      const fromId = String(f.properties?.fromId ?? '');
      const route = useRoutingStore.getState().routes.find((r) => r.id === routeId);
      if (!route) return null;
      const i = route.waypoints.findIndex((w) => w.id === fromId);
      if (i < 0 || i + 1 >= route.waypoints.length) return null;
      return { kind: 'xtd', routeId, fromId, a: route.waypoints[i].position, b: route.waypoints[i + 1].position };
    };

    const clearingHandleAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('pilotage-handle')) return undefined;
      const pad = 12;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['pilotage-handle'] },
      );
      return hits[0];
    };

    /** A boundary point of the anchor zone under the finger, if any. */
    const anchorZoneVertexAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('anchor-zone-vertex')) return undefined;
      const pad = 14;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['anchor-zone-vertex'] },
      );
      return hits[0];
    };

    /** An edge midpoint handle under the finger: tap it to split that edge. */
    const anchorZoneMidpointAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('anchor-zone-midpoint')) return undefined;
      const pad = 14;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['anchor-zone-midpoint'] },
      );
      return hits[0];
    };

    const measureVertexAt = (point: { x: number; y: number }) => {
      if (!map.getLayer('measure-vertex')) return undefined;
      const pad = 12;
      const hits = map.queryRenderedFeatures(
        [
          [point.x - pad, point.y - pad],
          [point.x + pad, point.y + pad],
        ],
        { layers: ['measure-vertex'] },
      );
      return hits[0];
    };

    const dragTargetAt = (point: { x: number; y: number }): DragTarget | null => {
      // While the ruler is active, dragging one of its vertices takes priority so
      // an existing point can be nudged rather than a new tap landing under it.
      if (useChartPlotterStore.getState().activeTool === 'measure') {
        const mvF = measureVertexAt(point);
        if (mvF?.properties) return { kind: 'measure', index: Number(mvF.properties.index) };
      }
      // Boundary handles come first while a zone is up. They sit over the swing
      // track and often over the boat itself, and a skipper prodding at the
      // boundary means the boundary.
      const azF = anchorZoneVertexAt(point);
      if (azF?.properties) return { kind: 'anchorZone', index: Number(azF.properties.index) };
      const wpF = waypointAt(point);
      if (wpF?.properties) {
        return { kind: 'route', routeId: String(wpF.properties.routeId), wpId: String(wpF.properties.wpId) };
      }
      const mkF = markAt(point);
      if (mkF?.properties) {
        return { kind: 'mark', markId: String(mkF.properties.markId) };
      }
      if (mobMarkAt(point)) {
        return { kind: 'mob' };
      }
      const xtF = xtdBandAt(point);
      // In Live the corridor spans most of the fairway, and while FOLLOWING it
      // covers exactly where a thumb goes to pan. Grabbing it there began a
      // drag that Live then (rightly) refused to move, which swallowed the
      // whole gesture: pan attempts that simply died, over and over
      // (fb-msnddglj-0, "locks me out of panning"). In Live the corridor only
      // grabs while ITS leg is deliberately being edited (the sheet's Adjust
      // button); width changes underway stay one deliberate step away.
      if (xtF) {
        if (useShellStore.getState().mode !== 'Live') return xtdLeg(xtF);
        const editing = useShellStore.getState().editingLegXtd;
        const sameLeg =
          editing &&
          String(xtF.properties?.routeId ?? '') === editing.routeId &&
          String(xtF.properties?.fromId ?? '') === editing.fromId;
        if (sameLeg) return xtdLeg(xtF);
      }
      const rF = ringAt(point);
      if (rF?.properties) {
        const pr = rF.properties;
        const centre: [number, number] = [Number(pr.cLon), Number(pr.cLat)];
        if (pr.markId != null) {
          return { kind: 'ring', ref: { kind: 'mark', markId: String(pr.markId) }, centre };
        }
        if (pr.routeId != null && pr.wpId != null) {
          return {
            kind: 'ring',
            ref: { kind: 'route', routeId: String(pr.routeId), wpId: String(pr.wpId) },
            centre,
          };
        }
      }
      const clF = clearingHandleAt(point);
      if (clF?.properties) {
        return { kind: 'clearing', id: String(clF.properties.id) };
      }
      const trF = transitHandleAt(point);
      if (trF?.properties) {
        return { kind: 'transit', id: String(trF.properties.id) };
      }
      const hbF = headingHandleAt(point);
      if (hbF?.properties) {
        return { kind: 'heading', id: String(hbF.properties.id) };
      }
      // EBL/VRM last, so every waypoint, mark, corridor and handle keeps priority
      // over the two long lines that stretch across the view. The VRM ring is the
      // more specific target, so it wins where it crosses the EBL.
      const vrmOs = vrmRingAt(point);
      if (vrmOs) return { kind: 'vrm', centre: vrmOs };
      const piOs = piLineAt(point);
      if (piOs) return { kind: 'pi', centre: piOs };
      const eblOs = eblLineAt(point);
      if (eblOs) return { kind: 'ebl', centre: eblOs };
      return null;
    };

    // Entering edit mode for a leg's XTD: highlight it and open the value sheet.
    const enterXtdEdit = (routeId: string, fromId: string) => {
      const cur = useShellStore.getState().editingLegXtd;
      if (!cur || cur.routeId !== routeId || cur.fromId !== fromId) {
        useShellStore.getState().setEditingLegXtd({ routeId, fromId });
        useShellStore.getState().openSheet('legxtd');
      }
    };

    const onMouseDown = (e: MapMouseEvent) => {
      if (usePilotagePlannerStore.getState().building) return;
      const dt = dragTargetAt(e.point);
      if (!dt) return;
      e.preventDefault(); // hold the target instead of panning the map
      if (dt.kind === 'xtd' && useShellStore.getState().mode !== 'Live') enterXtdEdit(dt.routeId, dt.fromId);
      beginDrag(dt, { x: e.point.x, y: e.point.y });
      if (
        useShellStore.getState().mode === 'Live' &&
        (dt.kind === 'route' || dt.kind === 'mark' || dt.kind === 'mob')
      ) {
        armLivePickup({ x: e.point.x, y: e.point.y });
      }
      map.on('mousemove', onWpDragMove);
      map.once('mouseup', onWpDragEnd);
    };
    map.on('mousedown', onMouseDown);

    mapRef.current = map;
    diagMap = map;

    // Keep the canvas the size of the screen, and write down every time it was
    // not. Edd came back to a chart filling the top half of the phone with black
    // underneath while every button sat where it should (05 Aug, build 304). The
    // buttons are React and the chart is a WebGL canvas, so the two disagreeing
    // about the height of the screen puts it in the canvas. MapLibre watches its
    // own container and should already have caught this, which is the reason to
    // watch rather than to reason about it.
    const stopCanvasWatch = watchMapCanvas(map, containerRef.current);

    // Dev-only handle on the map, so residency can actually be watched from
    // outside. Cells load and drop in response to panning with no visible
    // readout, which made it the one part of the chart stack that unit tests
    // covered and nothing else did. Stripped from any production build by the
    // DEV guard.
    if (import.meta.env.DEV) {
      (window as unknown as { __ardentMap?: maplibregl.Map }).__ardentMap = map;
    }

    // Diagnostic: surface MapLibre errors to the console so silent layer
    // failures don't disappear into the event channel.
    map.on('error', (e) => {
       
      console.error('[MapLibre error]', e?.error ?? e);
    });

    // ---- on-demand chart cells --------------------------------------------
    // Cells become resident for three reasons: the viewport, the water under the
    // boat, and the active route. The last two are safety rather than drawing,
    // which is why they are not filtered by what is on screen. The reasoning is
    // in src/map/cellResidency.ts.
    // Every source the registered hydrographic providers offer, read fresh each
    // time because packs come and go and (later) entitlement can lapse. The map
    // deliberately does not know who supplies a source: when licensed UKHO or
    // IC-ENC data arrives it registers a provider and appears here without this
    // file changing. See src/chart-sources/hydroProvider.ts.
    const cellSources = () => hydroSources();
    /**
     * Ask every source what it holds and rebuild the residency candidates. This
     * is one manifest read per source, so the whole NOAA set costs a single fetch
     * rather than 7,133, and it is cheap enough to redo when a pack comes or goes.
     */
    const listCellCandidates = async (): Promise<CellMeta[]> => {
      const list: CellMeta[] = [];
      const candidates: ResidencyCandidate[] = [];
      for (const src of cellSources()) {
        let cells: CellMeta[];
        try {
          cells = await src.listCells();
        } catch {
          // A source with no manifest yet (NOAA not ingested, a pack that will
          // not answer) is skipped, not fatal. Traced, because a source that
          // answers on one pass and not the next makes the candidate list flap,
          // and from the chart that is indistinguishable from a planner bug.
          traceResidency(`SRC ${src.id}: listCells failed, contributing nothing this pass`);
          console.info(`[MapView] no cells for source ${src.id} (skipped)`);
          continue;
        }
        for (const cell of cells) {
          list.push(cell);
          candidates.push({
            id: cell.id,
            sourceId: src.id,
            bounds: cell.bounds,
            scaleBand: cell.scaleBand,
            // The manifest's own feature count, which is what makes the
            // residency budget about memory rather than about cell count.
            // Measured first, manifest second: what we counted ourselves beats
            // what the chart set claimed, and covers sets that claim nothing.
            weight: cellCostRef.get(cell.id)?.features ?? cell.featureCount,
          });
        }
      }
      cellCandidatesRef.current = candidates;
      console.info('[MapView] cells available:', list.length);
      // The store gets every cell the sources offer, not just the resident ones,
      // so the data-quality panel still describes the whole chart set.
      setCells(list);
      await syncResidentCells();
      return list;
    };
    refreshCellsRef.current = listCellCandidates;
    /**
     * What each cell REALLY cost, measured when it was loaded: features parsed,
     * and the rough size of its payload.
     *
     * The manifest's featureCount was the first attempt and it is not good
     * enough: a chart set may not publish one, and the number the app plans on
     * should be the number the app measured. First sight of a cell is
     * uninformed; every pass after that is exact, which is what makes the
     * budget converge on a dense set instead of guessing at it.
     */
    const cellCostRef = new Map<string, { features: number; bytes: number }>();
    let residencyRunning = false;
    let residencyQueued = false;
    const syncResidentCells = async (): Promise<void> => {
      // Loading is async and pans arrive in bursts, so overlapping runs would
      // fight over the same sources. Coalesce instead: note that another run is
      // wanted, and loop once the current one finishes.
      if (residencyRunning) {
        residencyQueued = true;
        return;
      }
      residencyRunning = true;
      try {
        // A hard ceiling on passes per invocation. The do-while exists to
        // coalesce a burst of pans, but it trusts whatever sets residencyQueued,
        // and on 12 Aug a skip that re-queued itself turned that trust into a
        // spin that hung the app on its boot screen. A run of chained passes is
        // legitimate; an unbounded one never is. Anything still outstanding is
        // picked up by the next moveend, so the cost of stopping is one settle.
        const MAX_PASSES = 8;
        let passes = 0;
        do {
          if (++passes > MAX_PASSES) {
            traceResidency(`STOP after ${MAX_PASSES} chained passes; waiting for the next move`);
            break;
          }
          residencyQueued = false;
          if (cellCandidatesRef.current.length === 0) break;
          const epoch = styleEpochRef.current;
          const sourceById = new Map(cellSources().map((s) => [s.id, s]));
          const b = map.getBounds();
          const rs = useRoutingStore.getState();
          const wantedRoutes = new Set(
            [rs.activeRouteId, rs.followRouteId, useRoutePlanStore.getState().plannedRouteId].filter(
              (id): id is string => !!id,
            ),
          );
          // Heap-aware backoff. The heartbeat already samples the heap for the
          // black box; this is the first thing that ACTS on it. Android reclaims
          // the renderer when the WebView gets greedy, and the reclaim reads as
          // a crash from the deck (four of them in Edd's 11 Aug session), so as
          // the heap fills we hold fewer cells rather than pressing on and
          // hoping. Below two thirds nothing changes.
          const { heapMB, heapLimitMB } = readHeap();
          const heapFrac = heapMB != null && heapLimitMB ? heapMB / heapLimitMB : 0;
          const heapCells =
            heapFrac > 0.85 ? 4 : heapFrac > 0.72 ? 6 : heapFrac > 0.66 ? 8 : DEFAULT_MAX_CELLS;
          const plan = planResidency({
            maxCells: heapCells,
            candidates: cellCandidatesRef.current,
            view: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
            zoom: map.getZoom(),
            ownship: useChartPlotterStore.getState().ownship.position,
            routes: rs.routes
              .filter((r) => wantedRoutes.has(r.id))
              .map((r) => r.waypoints.map((w) => w.position)),
            current: cellsLoadedRef.current,
          });
          // Never truncate silently: if the cap turned cells away, say so.
          if (plan.cappedOut > 0) {
            console.info(
              `[MapView] residency cap reached, ${plan.cappedOut} further cells not loaded`,
            );
          }
          // One line per plan: zoom, wanted band, the plan's view, every kept
          // cell with its band and reason, drops, cap pressure. ALWAYS built,
          // because it feeds the residency trace the in-app feedback tool
          // attaches to every bug report. Residency has no visible readout, and
          // both of its field bugs so far were diagnosable only from exactly
          // this line. The first time round it was dev-only, and the phone,
          // where it mattered, had no console. The view is in the line because
          // the second bug was invisible without it: plans alternating between
          // the harbour on screen and a boat four thousand miles away.
          {
            const z = map.getZoom();
            const vb = map.getBounds();
            const line =
              `z${z.toFixed(2)} want b${preferredBand(z)} cand=${cellCandidatesRef.current.length} ` +
              `view=[${vb.getWest().toFixed(2)},${vb.getSouth().toFixed(2)},${vb.getEast().toFixed(2)},${vb.getNorth().toFixed(2)}] | keep ` +
              plan.keep
                .map((c) => `${c.id}(b${c.scaleBand},${(plan.reasons.get(c.id) ?? '?')[0]})`)
                .join(' ') +
              (plan.remove.length > 0 ? ` | drop ${plan.remove.join(' ')}` : '') +
              (plan.cappedOut > 0 ? ` | capped ${plan.cappedOut}` : '') +
              // Weight and heap in the box: the two numbers that would have
              // named the America crash on the first report rather than the
              // fourth.
              ` | w=${Math.round(plan.keptWeight / 1000)}k` +
              // What is actually resident, measured, plus what MapLibre itself
              // is carrying. The heap figure below is the MAIN THREAD's only:
              // GeoJSON tiling runs in a worker and the drawing in the GPU
              // process, and neither is visible to performance.memory. On
              // 11 Aug the renderer was reclaimed with a 14 MB heap, which is
              // exactly what a main-thread-only reading looks like when the
              // memory is somewhere else. src/lyr say where to look next.
              ` meas=${Math.round(
                [...cellsLoadedRef.current].reduce(
                  (t, id) => t + (cellCostRef.get(id)?.features ?? 0),
                  0,
                ) / 1000,
              )}k` +
              (() => {
                try {
                  const st = map.getStyle();
                  return ` src=${Object.keys(st?.sources ?? {}).length} lyr=${(st?.layers ?? []).length}`;
                } catch {
                  return '';
                }
              })() +
              (plan.weightTrimmed > 0 ? ` wtrim=${plan.weightTrimmed}` : '') +
              (heapMB != null ? ` heap=${heapMB}/${heapLimitMB}` : '') +
              (heapCells !== DEFAULT_MAX_CELLS ? ` cap=${heapCells}` : '');
            traceResidency(line);
            if (import.meta.env.DEV) console.debug('[residency] ' + line);
          }
          for (const id of plan.remove) {
            removeCellFromMap(map, id);
            cellsLoadedRef.current.delete(id);
            cellsRef.current.delete(id);
          }
          let changed = plan.remove.length > 0;
          // Did the resident SET change this pass (rather than merely being
          // re-planned to the same answer)? The heavy rebuilds below hang off
          // this, so a settled view costs nothing.
          let residentChanged = plan.remove.length > 0;
          // Load coarse before fine, so most passes append in quilt order and
          // the anchor below is only needed when a coarse cell arrives late.
          const bandOf = new Map(cellCandidatesRef.current.map((c) => [c.id, c.scaleBand]));
          const sortedAdds = [...plan.add].sort((a, b) => (bandOf.get(a) ?? 0) - (bandOf.get(b) ?? 0));
          // At most a few new cells per pass. Loading a full dozen dense cells
          // back to back is a memory spike with no gap for the renderer to
          // breathe or for eviction to run, which is how a pan into American
          // water took the WebView out. The rest are not abandoned: the pass
          // re-queues itself and picks them up on the next turn, which also
          // lets a pan that has moved on re-plan rather than finish loading
          // chart for water we have already left.
          const ADDS_PER_PASS = 3;
          const adds = sortedAdds.slice(0, ADDS_PER_PASS);
          if (sortedAdds.length > adds.length) residencyQueued = true;
          for (const id of adds) {
            const candidate = cellCandidatesRef.current.find((c) => c.id === id);
            const src = candidate ? sourceById.get(candidate.sourceId) : undefined;
            if (!src) continue;
            try {
              const fc = await src.loadCell(id);
              // Measure it whatever happens next: we have paid for the parse,
              // so the planner may as well learn what this cell really costs.
              const measured = fc.features?.length ?? 0;
              cellCostRef.set(id, { features: measured, bytes: measured * 400 });
              // THE PAN HAS MOVED ON. This is the churn that kills the renderer
              // mid-pan: a cell is planned, fetched, and then handed to MapLibre
              // to tile in its worker even though the view left that water while
              // it was loading, and the very next pass tears it straight back
              // down. Across an ocean that repeats for every cell we fly over.
              // A cell the boat or the route needs is added regardless, because
              // that is safety and not scenery.
              const stillWanted =
                plan.reasons.get(id) !== 'view' ||
                (() => {
                  try {
                    const nb = map.getBounds();
                    const now: [number, number, number, number] = [
                      nb.getWest(),
                      nb.getSouth(),
                      nb.getEast(),
                      nb.getNorth(),
                    ];
                    // The view has not moved at all since this plan was made,
                    // so of course the cell is still wanted. Checking this
                    // FIRST is what stops the skip from ever arguing with the
                    // planner about a view neither of them has changed.
                    const planView: [number, number, number, number] = [
                      b.getWest(),
                      b.getSouth(),
                      b.getEast(),
                      b.getNorth(),
                    ];
                    if (planView.every((v, i) => v === now[i])) return true;
                    // Pad by the SAME margin the planner uses when it chooses
                    // view cells. Testing the unpadded bounds was an infinite
                    // loop: a cell in the padding ring is planned (padded),
                    // rejected here (unpadded), re-queued, and planned again
                    // for ever, which hung the app on the boot screen.
                    const dx = (now[2] - now[0]) * 0.25;
                    const dy = (now[3] - now[1]) * 0.25;
                    return boundsIntersect(candidate!.bounds, [
                      now[0] - dx,
                      now[1] - dy,
                      now[2] + dx,
                      now[3] + dy,
                    ]);
                  } catch {
                    return true;
                  }
                })();
              if (!stillWanted) {
                // Deliberately NOT re-queueing. The move that changed the view
                // has already queued its own pass through the settle timer, and
                // re-queueing here is what turned a skip into a spin.
                traceResidency(`SKIP ${id}: view moved on during load`);
                continue;
              }
              // A style swap during that await makes this generation stale; stop
              // before addSource/addLayer runs against the new, parsing style.
              if (styleEpochRef.current !== epoch) return;
              // The view can also unmount mid-load, and the cleanup calls
              // map.remove() then nulls the ref. Adding a source to a removed map
              // throws from inside MapLibre, which is what filled the console on
              // every dev boot: StrictMode mounts, tears down and remounts, and
              // the first mount's cell loads landed on the corpse of its map.
              if (mapRef.current !== map) return;
              // Insert at the band's place in the quilt, not on top: a coarse
              // cell arriving after a fine one must slide UNDER it, or its
              // opaque fills blank the harbour the user is looking at.
              const loadedBands = new Map<string, number>();
              for (const loadedId of cellsLoadedRef.current) {
                const b = bandOf.get(loadedId);
                if (b !== undefined) loadedBands.set(loadedId, b);
              }
              const anchor = candidate
                ? cellAnchorLayerId(map.getStyle()?.layers ?? [], loadedBands, candidate.scaleBand)
                : undefined;
              // Timed per cell. This is the prime suspect for the NOAA freeze:
              // it annotates the features, hands MapLibre a source that may be
              // megabytes, and then adds about sixty layers to a style that
              // already has hundreds — and addLayer revalidates the style each
              // time. The trace names it when it is slow.
              {
                const t0 = performance.now();
                addCellToMap(map, id, fc, paletteRef.current, visibilityRef.current, symbolSetRef.current, anchor);
                const ms = Math.round(performance.now() - t0);
                if (ms >= 50) traceResidency(`SLOW addCell ${id} ${ms}ms (${measured} features)`);
              }
              cellsLoadedRef.current.add(id);
              cellsRef.current.set(id, fc);
              changed = true;
              residentChanged = true;
              // Places are rebuilt from the resident cells below, not
              // accumulated here: see the setPlaces call.
              void 0;
            } catch (cellErr) {
              // Into the trace as well as the console: a cell that keeps failing
              // to load looks identical to a residency bug from the deck of a
              // boat, and the trace is what tells the two apart in a bug report.
              traceResidency(`FAIL ${id}: ${String((cellErr as Error)?.message ?? cellErr).slice(0, 120)}`);
              console.error(`[MapView] cell ${id} failed to load (skipped)`, cellErr);
            }
          }
          if (changed) {
            // TIME THE HEAVY STEPS. The America freeze is a blocked main
            // thread, not memory: the app sits there for minutes with a 12 MB
            // heap, and only ever on NOAA, so something here does work
            // proportional to real cell size. Rather than guess a fourth time,
            // each step is timed and anything slow is named in the trace, so
            // the next dump says which one rather than leaving me to theorise.
            const timings: string[] = [];
            const timed = <T,>(label: string, fn: () => T): T => {
              const t0 = performance.now();
              try {
                return fn();
              } finally {
                const ms = Math.round(performance.now() - t0);
                if (ms >= 50) timings.push(`${label}:${ms}ms`);
              }
            };
            // Tell the spatial index its input has changed, so the depth and
            // danger alarms rebuild against the cells that are actually loaded.
            cellsGenRef.current += 1;
            // Rebuilt from the resident cells, so the index cannot grow for
            // ever — but only when the RESIDENT SET has changed, not on every
            // pass. Walking a dozen dense cells' worth of features on each pass
            // was my own regression: on a Scottish cell it is nothing, on
            // American cells it is hundreds of thousands of features chewed on
            // the main thread every time the view settles.
            if (residentChanged) {
              timed('places', () => {
                placesRef.current.clear();
                for (const place of extractNamedPlaces(cellsRef.current.values())) {
                  placesRef.current.set(place.id, place);
                }
              });
            }
            useSearchIndexStore.getState().setPlaces(Array.from(placesRef.current.values()));
            // Light sectors are rebuilt from EVERY resident cell's features,
            // so this walk grows with the chart set exactly as the freeze does.
            // Gated on the resident set actually changing, for the same reason
            // the places rebuild above is: re-deriving the same answer from the
            // same cells is pure cost.
            if (residentChanged) {
              timed('lightsectors', () =>
                ensureLightSectorsLayer(map, Array.from(cellsRef.current.values()), paletteRef.current),
              );
            }
            // A finest-band cell was appended on top of everything (that is what
            // no anchor id means), so put the boat and the swing circle straight
            // back above it rather than waiting for the next fix to do it.
            raiseAnchorWatchLayers(map);
            raisePilotageLayers(map);
            raiseOwnshipLayers(map);
            if (timings.length > 0) traceResidency(`SLOW ${timings.join(' ')}`);
          }
        } while (residencyQueued);
      } finally {
        residencyRunning = false;
      }
    };
    syncCellsRef.current = syncResidentCells;

    map.once('load', async () => {
       
      console.info('[MapView] map loaded, fetching cells…');
      // Tell the first-boot loading screen the base map is up. Set here at the
      // top of the load handler (not after cell fetching) so a slow or failed
      // cell load can never hold the splash open — the map is already drawable.
      useBootStore.getState().setMapReady();
      // The generation this initial mount belongs to. If a palette/style swap lands
      // while cells are still loading (a vector load in flight), setStyle wipes what
      // we've added and rebuilds; continuing to addSource/addLayer here would hit the
      // new, still-parsing style and throw. Bail when our generation goes stale — the
      // palette re-apply then mounts the overlays for the new style.
      const loadEpoch = styleEpochRef.current;
      try {
        const list = await listCellCandidates();
        console.info(
          '[MapView] cells resident:',
          cellsLoadedRef.current.size,
          '- mounting overlay layers',
        );
        // Last await was the cell loads above; if a style swap superseded us since,
        // don't mount overlays onto the old generation's style.
        if (styleEpochRef.current !== loadEpoch) return;
        const ui = getPalette(palette);
        registerPlotIcons(map, ui.UI_ACCENT, ui.CHBLK);
        ensureOwnshipLayer(map, ownship, tracks, palette, useSettingsStore.getState().ownVectorMin, deviceFixStale(), currentOwnVectors());
        ensureObjectsLayer(map, currentGraphObjects(), palette);
        ensureRouteLayer(map, routes, activeRouteId, palette);
        ensureMarksLayer(map, marks, palette);
        ensureNotesLayer(map, useNotesStore.getState().notes, palette);
        setDestinationMarker(map, destination, () => useShellStore.getState().openSheet('destination'));
        ensurePilotageLayer(map, [], [], [], null, null, null, new Set(), palette);
        // Authored-from-chart TSS overlay (no-ops where the local file is absent).
        await ensureTssOverlay(map, palette);
        ensureLightSectorsLayer(map, Array.from(cellsRef.current.values()), palette);
        ensureSafetyLayer(
          map,
          { anchor, mob, dangers, ownship, settings: safetySettings },
          palette,
        );
        ensureMobSearchAreaLayer(map, useMobSearchStore.getState().overlay, palette);
        ensureAnchorWatchLayer(map, currentAnchorWatchInput(!!useAlarmsStore.getState().live.anchor), palette);
        ensureTideLayer(map, showStream, streamArrowsAt(streamSource, streamTimeMs), palette);
        ensureWeatherLayer(map, showWeather, weatherSamples, windStyle);
        ensurePinnedWindLayer(map, pinnedWind);
        ensureRangeRingsLayer(map, palette);
        ensureEblVrmLayer(map, palette);
        ensurePilotagePlanLayer(map, palette);
        ensureMeasureLayer(map, palette);
        ensureSavedMeasuresLayer(map, palette);
        ensureAisLayer(map, [], new Set(), null, 6, Date.now(), palette);
        updateClearingDepthHighlight(map, visibleClearingDepthValues(), palette);
        updateContourHighlight(map, useContourHighlightStore.getState().values, palette);
      updateDryingCover(map, dryingTideNowM(), palette, symbolSet);
         
        console.info(
          '[MapView] overlays mounted. sources:',
          Object.keys(map.getStyle().sources ?? {}),
          'layers:',
          (map.getStyle().layers ?? []).length,
        );

        const overview = list.find((c) => c.scaleBand === 3) ?? list[0];
        if (overview) {
          map.fitBounds(
            [
              [overview.bounds[0], overview.bounds[1]],
              [overview.bounds[2], overview.bounds[3]],
            ],
            { padding: 40, duration: 0 },
          );
        }
        applyChartSource('source');
        applySeamark();
      } catch (err) {
        console.error('failed to load training cells', err);
      }
    });

    // The cell-set ref holds one stable Set (only mutated, never reassigned),
    // so capture it here and clear that in cleanup (satisfies exhaustive-deps).
    const cellsLoaded = cellsLoadedRef.current;
    return () => {
      map.off('mousemove', onMove);
      map.off('moveend', onMoveEnd);
      // A settle timer that outlives the map would plan residency against a
      // map that has been removed.
      if (settleTimer !== null) clearTimeout(settleTimer);
      clearTimeout(healthyTimer);
      map.off('dragstart', onUserPan);
      map.off('zoomend', onZoomEnd);
      map.off('click', onClick);
      map.off('contextmenu', onContextMenu);
      map.off('touchstart', onTouchStart);
      map.off('touchmove', onTouchMove);
      map.off('touchend', onTouchEnd);
      map.off('touchcancel', onTouchEnd);
      window.removeEventListener('acp:thawed', onThawed);
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onWpDragMove);
      map.off('touchmove', onWpDragMove);
      clearLongPress();
      stopCanvasWatch();
      canvasEl.removeEventListener('webglcontextlost', onContextLost, false);
      canvasEl.removeEventListener('webglcontextrestored', onContextRestored, false);
      setChartedDepthQuery(null);
      setChartedDepthDetailQuery(null);
      setChartedZocQuery(null);
      setActiveMap(null);
      diagMap = null;
      map.remove();
      mapRef.current = null;
      cellsLoaded.clear();
    };
    // Mount-once effect: it builds the map and must not re-run, so the deps are
    // intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track the palette the map was last styled with so we don't fire setStyle
  // on initial mount (the map is already constructed with the correct style;
  // re-applying it races with the load handler and ends up putting the cell
  // layers on top of the overlays, covering them).
  // Hand the chart's memory back while the app is behind.
  //
  // A chart is a live WebGL map, and backgrounded it goes on holding its GL
  // context, its tile cache and every texture in it while using none of them.
  // Android counts all of that when it decides whose memory to take, and on the
  // morning this was written it took ours: renderer gone, didCrash=false, after a
  // night on an anchor watch. What the skipper then gets is a bare grey window
  // for as long as the activity takes to rebuild itself, which on a phone that is
  // short of memory in the first place is a long time.
  //
  // So we let go first. setStyle to the bare base drops every source and layer
  // and the tiles behind them, which is the bulk of what we were holding, and it
  // is the same thing a palette swap already does dozens of times a passage.
  // Coming back rebuilds through that same palette-swap path rather than a second
  // rebuild route nobody exercises: the counter below is folded into the style
  // key, so a resume looks to that effect exactly like a switch from dusk to
  // night.
  //
  // Not immediately, though. Glancing at the almanac app and coming straight back
  // should not cost a chart rebuild, and a phone does not start hunting for
  // memory in the first few seconds either. So there is a wait, and a return
  // inside it costs nothing at all.
  const appActive = useAppActiveStore((s) => s.active);
  const [chartRebuild, setChartRebuild] = useState(0);
  const chartReleasedRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (appActive) {
      // Nothing was let go, so there is nothing to put back.
      if (!chartReleasedRef.current) return;
      chartReleasedRef.current = false;
      setChartRebuild((n) => n + 1);
      return;
    }

    const timer = setTimeout(() => {
      const m = mapRef.current;
      if (!m || !isStyleReady(m)) return;
      // Bump the generation BEFORE the swap, so a cell load still in flight sees a
      // stale epoch and bails rather than adding a source to a style we are in the
      // middle of dropping.
      styleEpochRef.current = nextEpoch(styleEpochRef.current);
      try {
        m.setStyle(buildBaseStyle(palette));
        chartReleasedRef.current = true;
      } catch (err) {
        // A chart we failed to release is a chart that still draws. Say so and
        // carry on; the worst case is the memory we were holding before.
        console.error('[chart] could not release the chart on going behind', err);
      }
    }, RELEASE_CHART_AFTER_MS);
    return () => clearTimeout(timer);
  }, [appActive, palette]);

  const lastStyleKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) {
      // Record the mounted palette even before the style has parsed (isStyleReady is
      // false for the first tick after construction). Otherwise the ref stays null,
      // the first REAL palette switch is mistaken for the initial mount, setStyle is
      // skipped, and the chart keeps its day colours at dusk/night while only the
      // tide-aware drying band recolours — "dusk looks like day but broken shallows".
      if (map && lastStyleKeyRef.current === null) lastStyleKeyRef.current = palette;
      return;
    }
    // The rebuild counter rides in the key so coming back from the background
    // re-enters this effect exactly as a palette change does. See the release
    // block above.
    const styleKey = `${palette}#${chartRebuild}`; // symbol set is re-skinned in place, not via setStyle
    if (lastStyleKeyRef.current === null) {
      lastStyleKeyRef.current = styleKey;
      return;
    }
    if (lastStyleKeyRef.current === styleKey) return;
    lastStyleKeyRef.current = styleKey;
    // Bump the style generation BEFORE swapping the style; the re-apply below
    // captures it. If another chart/palette change swaps the style again while we
    // re-apply, our generation goes stale and the work bails instead of mutating the
    // new (still-parsing) style — that addSource/addLayer -> Style._checkLoaded
    // "Style is not done loading." throw is what used to blank the chart.
    const epoch = (styleEpochRef.current = nextEpoch(styleEpochRef.current));
    const stillCurrent = () =>
      shouldApplyStyleWork({
        styleReady: isStyleReady(map),
        capturedEpoch: epoch,
        currentEpoch: styleEpochRef.current,
      });
    map.setStyle(buildBaseStyle(palette));
    const handler = async () => {
      try {
      // Re-add in band order (coarse first), so the rebuilt stack quilts
      // correctly without needing anchors: the whole set goes back in one pass.
      const swapBandOf = new Map(cellCandidatesRef.current.map((c) => [c.id, c.scaleBand]));
      const reAddIds = [...cellsLoadedRef.current].sort(
        (a, b) => (swapBandOf.get(a) ?? 0) - (swapBandOf.get(b) ?? 0),
      );
      for (const cellId of reAddIds) {
        try {
          // Re-add from the in-memory cache so cells from any source (training or
          // NOAA) come back correctly after a palette swap.
          const fc = cellsRef.current.get(cellId) ?? (await trainingChartSource.loadCell(cellId));
          // loadCell may have yielded long enough for a newer setStyle to land. If so
          // this generation is stale — stop before addCellToMap's addSource/addLayer
          // runs against the new style (the throw that used to blank the chart).
          if (!stillCurrent()) return;
          addCellToMap(map, cellId, fc, palette, visibility, symbolSet);
        } catch (err) {
          console.error('re-add cell after palette swap failed', cellId, err);
        }
      }
      // Everything below mutates the style synchronously, so one generation check up
      // front covers the whole overlay re-add.
      if (!stillCurrent()) return;
      const ui = getPalette(palette);
      registerPlotIcons(map, ui.UI_ACCENT, ui.CHBLK);
      ensureOwnshipLayer(map, ownship, tracks, palette, useSettingsStore.getState().ownVectorMin, deviceFixStale(), currentOwnVectors());
      ensureObjectsLayer(map, currentGraphObjects(), palette);
      {
        const rs = useRoutingStore.getState();
        ensureRouteLayer(
          map,
          routes.filter((r) => !rs.hiddenRouteIds.includes(r.id) || r.id === rs.followRouteId),
          activeRouteId,
          palette,
        );
      }
      ensureMarksLayer(map, marks, palette);
      ensureNotesLayer(map, useNotesStore.getState().notes, palette);
      setDestinationMarker(map, destination, () => useShellStore.getState().openSheet('destination'));
      {
        const ps = usePilotageStore.getState();
        ensurePilotageLayer(
          map,
          ps.clearings.filter((c) => c.planId == null || !ps.hiddenPlanIds.includes(c.planId)),
          ps.transits.filter((t) => t.planId == null || !ps.hiddenPlanIds.includes(t.planId)),
          ps.headingBearings.filter((h) => h.planId == null || !ps.hiddenPlanIds.includes(h.planId)),
          ps.activeClearingId,
          ps.activeTransitId,
          ps.activeHeadingId,
          new Set(),
          palette,
        );
      }
      ensureLightSectorsLayer(map, Array.from(cellsRef.current.values()), palette);
      ensureSafetyLayer(
        map,
        { anchor, mob, dangers, ownship, settings: safetySettings },
        palette,
      );
      ensureMobSearchAreaLayer(map, useMobSearchStore.getState().overlay, palette);
      ensureAnchorWatchLayer(map, currentAnchorWatchInput(!!useAlarmsStore.getState().live.anchor), palette);
      ensureTideLayer(map, showStream, streamArrowsAt(streamSource, streamTimeMs), palette);
        ensureWeatherLayer(map, showWeather, weatherSamples, windStyle);
        ensurePinnedWindLayer(map, pinnedWind);
      ensureRangeRingsLayer(map, palette);
      ensureEblVrmLayer(map, palette);
      ensurePilotagePlanLayer(map, palette);
      ensureMeasureLayer(map, palette);
      ensureSavedMeasuresLayer(map, palette);
      // setStyle wiped the AIS source/layers with everything else; re-create them
      // (empty for now) so the throttled AIS tick repopulates them instead of the
      // targets vanishing after a palette swap. ensureAisLayer re-seats them on top.
      ensureAisLayer(map, [], new Set(), null, 6, Date.now(), palette);
      updateClearingDepthHighlight(map, visibleClearingDepthValues(), palette);
      updateContourHighlight(map, useContourHighlightStore.getState().values, palette);
      updateDryingCover(map, dryingTideNowM(), palette, symbolSet);
      // The base style was rebuilt; re-skin the buoy glyphs for the active set
      // (deferred internally until the style settles).
      setChartSymbolStyle(map, glyphStyleFor(symbolSet));
      // The palette setStyle wiped the base/raster/tiles and the seamark overlay;
      // force a full re-apply so they come back.
      lastChartSrcRef.current = '';
      applyChartSource('source');
      applySeamark();
      } catch (err) {
        console.error('[chart] palette re-apply failed', err);
      }
    };
    const runWhenReady = () => {
      // A newer setStyle superseded this one: drop out WITHOUT re-registering the
      // styledata listener, so a stale generation neither re-applies nor leaves a
      // listener bound to the old style firing against the new one.
      if (styleEpochRef.current !== epoch) return;
      if (isStyleReady(map)) {
        void handler();
        return;
      }
      map.once('styledata', runWhenReady);
    };
    runWhenReady();
    return () => {
      map.off('styledata', runWhenReady);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palette, chartRebuild]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    // Wrapped: this runs on a provider swap. If a WebGL context loss (memory
    // pressure while churning heavy chart providers) has dropped the GL context,
    // style._loaded stays true so isStyleReady passes, but a maplibre-internal
    // mutation can still throw. Unwrapped, that throw would reach the ErrorBoundary
    // and blank the app ("keeps crashing when I change charts"). Swallow it and
    // leave the layers as-is; the context-restore handler redraws.
    try {
      applyVisibility(map, visibility);
      // A raster provider keeps the vector cells hidden (under the scan, or under a
      // satellite overlay). A vector provider with satellite ON is in blend mode, so
      // re-hide just the base fills the toggles may have re-shown, keeping the photo
      // visible with the chart detail on top.
      if (getProvider(chartProviderId).kind === 'raster') setCellLayersHidden(map, true);
      else if (satEnabled) setCellBaseFillsHidden(map, true);
    } catch (err) {
      console.error('[chart] visibility apply failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, groupVisibility, chartProviderId, satEnabled]);

  // Chart source: show the selected raster provider's best chart for the current
  // view (or a manual override) beneath the overlays and hide the vector cells;
  // or clear the raster and restore the cells when the vector provider is active.
  //
  // Applied imperatively (applyChartSource below), never through React state, so a
  // pan/zoom follows the view without re-rendering this component. A source change
  // does the full re-apply; a settled move does only the raster auto-pick.
  const lastChartSrcRef = useRef('');
  // The raster chart currently shown, so the auto-pick can be sticky (hold it
  // across small pans/zooms) rather than re-scoring from scratch each move.
  const currentRasterIdRef = useRef<string | null>(null);

  // Apply the chart source imperatively (NO React state), so a pan/zoom never
  // re-renders this component. Reads everything from the stores at call time, so
  // it is safe to call from map event handlers registered once at mount.
  //   reason 'source' : a provider / override / satellite change — full re-apply.
  //   reason 'move'   : a pan/zoom settled — only the raster auto-pick can change
  //                     anything (tiles & vector bases are already in place and the
  //                     satellite is global), so tiles/vector/sat do nothing here.
  const applyChartSource = useCallback((reason: 'source' | 'move') => {
    try {
    const map = mapRef.current;
    // Gate on the PARSED style (Style._loaded), not isStyleLoaded(): the running
    // position sim + streaming base/seamark tiles keep sources loading, so
    // isStyleLoaded() flickers false and would make this no-op during live use.
    if (!map || !isStyleReady(map)) return;
    const cps = useChartPlotterStore.getState();
    const providerId = cps.chartProviderId;
    const overrideId = cps.chartOverrideId;
    const cs = useChartSourcesStore.getState();
    const provider = resolveProvider(providerId, cs.bsbCharts, cs.noaaCharts, cs.packCharts);
    const sat = useSatelliteStore.getState();
    const satOn = sat.enabled;
    const satSource = resolveSatelliteSource(sat.sourceId, sat.apiKey);
    const vis: LayerVisibilityState = {
      categories: cps.displayCategories,
      groups: { ...defaultGroupVisibility(), ...cps.groupVisibility },
      declutterLevel: cps.declutterLevel,
    };
    const makeSatelliteBase = (): ActiveTileBasemap => ({
      id: satSource.id,
      url: buildTileUrl(satSource.urlTemplate, satelliteKey(sat.apiKey)),
      tileSize: satSource.tileSize,
      maxzoom: satSource.maxzoom,
      attribution: satSource.attribution,
      dim: sat.dim,
      opacity: sat.opacity,
    });
    const srcKey = `${providerId}|${overrideId ?? ''}|${satOn ? satSource.id : 'off'}`;
    const srcChanged = lastChartSrcRef.current !== srcKey;

    // The raster quilt — the one thing a pan/zoom can change.
    const applyRasterPick = () => {
      const b = map.getBounds();
      const view: [number, number, number, number] = [
        b.getWest(),
        b.getSouth(),
        b.getEast(),
        b.getNorth(),
      ];
      // A manual override pins one chart; otherwise lay the whole set that covers
      // this view, coarsest underneath, so the provider behaves as one source.
      const override = overrideId ? provider.charts.find((x) => x.id === overrideId) : undefined;
      const charts = override ? [override] : quiltCharts(provider, view, map.getZoom());
      const chart = charts[charts.length - 1] ?? null;
      if (!chart) {
        if (srcChanged) {
          setRasterCharts(map, []);
          applyVisibility(map, vis);
          // There ARE charts here, we're just too far out for their tiles: the
          // Antares harbour plans are cut from about z12 down, so at z11 they draw
          // nothing. Say so rather than flying somewhere else, because moving the
          // boat's view off is the last thing wanted when the charts are right here.
          if (chartsInView(provider, view).length > 0) {
            useShellStore
              .getState()
              .showToast(`Zoom in to see the ${provider.name} charts covering here`);
            currentRasterIdRef.current = null;
            return;
          }
          const cov = providerCoverageBounds(provider.charts);
          if (cov) {
            map.fitBounds(
              [
                [cov[0], cov[1]],
                [cov[2], cov[3]],
              ],
              { padding: 48, duration: 900, maxZoom: 11 },
            );
            useShellStore.getState().showToast(`No ${provider.name} chart here — moved to where this set covers`);
          }
        }
        currentRasterIdRef.current = null;
        return;
      }
      // The topmost (most detailed) chart is the one we are really looking at, so
      // that is the one the rest of the UI names.
      currentRasterIdRef.current = chart.id;
      setRasterCharts(
        map,
        charts.map((ch, i) => ({
          id: ch.id,
          path: ch.path,
          source: ch.source,
          minZoom: ch.tileMinZoom,
          maxZoom: ch.tileMaxZoom,
          bounds: ch.bounds,
          // One credit for the set, on the bottom chart, rather than the same notice
          // repeated once per chart in the quilt.
          attribution: i === 0 ? provider.attribution : '',
        })),
      );
      setCellLayersHidden(map, true);
    };

    if (reason === 'move') {
      // A move never changes the source key; only the raster pick can shift.
      if (provider.kind === 'raster' && !satOn) applyRasterPick();
      return;
    }

    // reason === 'source': full re-apply.
    lastChartSrcRef.current = srcKey;

    if (provider.kind === 'tiles') {
      // OpenSeaMap: a light VECTOR base (GPU-rendered, tiny tiles) with the seamark
      // overlay composited on top. Clear any raster chart first.
      setRasterChart(map, null);
      const base = provider.baseVector;
      if (satOn) {
        // Satellite ON over OpenSeaMap: show the photo AS the base with the seamarks
        // composited on top (the Savvy Navvy look). The vector land/water base draws
        // an opaque land background + water fill, so leaving it up would bury the
        // imagery under it — that's why turning satellite on used to do nothing here.
        // Drop the vector base and let the satellite tiles be the base; the seamark
        // effect (which re-runs on satEnabled) re-seats the marks above this new base.
        setVectorBasemap(map, null);
        setTileBasemap(map, makeSatelliteBase());
      } else {
        // No satellite: clear any imagery base and lay the vector base at the bottom.
        setTileBasemap(map, null);
        if (base) {
          setVectorBasemap(map, {
            url: base.url,
            maxzoom: base.maxzoom,
            attribution: provider.attribution ?? '',
            // MapTiler glyphs → the base shows town/road labels (MapTiler serves our
            // Open Sans, so chart text keeps rendering); labels sit under the seamarks.
            glyphs: maptilerGlyphsUrl(),
          });
        }
      }
      // OpenSeaMap's own vector/satellite base already fills the whole view, so the
      // rough fallback would only sit hidden under it — clear it here.
      setFallbackBasemap(map, null);
      setCellLayersHidden(map, true);
      currentRasterIdRef.current = null;
      return;
    }

    if (provider.kind === 'raster') {
      setVectorBasemap(map, null);
      if (satOn) {
        setRasterChart(map, null);
        setTileBasemap(map, makeSatelliteBase());
        // Satellite is the base and covers everything; no rough fallback needed.
        setFallbackBasemap(map, null);
        setCellLayersHidden(map, true);
        currentRasterIdRef.current = null;
        return;
      }
      setTileBasemap(map, null);
      // Lay the rough locator base at the bottom so any gap between raster tiles (or
      // an area the picked chart doesn't reach) still shows coastline, not blank.
      setFallbackBasemap(map, maptilerFallbackBasemap());
      applyRasterPick();
      return;
    }

    // VECTOR provider: no per-chart raster either way.
    setRasterChart(map, null);
    setVectorBasemap(map, null);
    if (satOn) {
      setTileBasemap(map, makeSatelliteBase());
      // Satellite is the base and covers everything; no rough fallback needed.
      setFallbackBasemap(map, null);
      if (srcChanged) applyVisibility(map, vis);
      setCellBaseFillsHidden(map, true);
    } else {
      setTileBasemap(map, null);
      // Lay the rough locator base at the bottom so sailing off the edge of the ENC
      // cells (or an area with no pack) still shows coastline, not a blank background.
      setFallbackBasemap(map, maptilerFallbackBasemap());
      if (srcChanged) applyVisibility(map, vis);
    }
    currentRasterIdRef.current = null;
    } catch (err) {
      // Belt-and-braces only: correct sequencing (the style-generation guard on the
      // async re-apply) is what now prevents the chart-switch crash. This no longer
      // hides a routine race; it just makes a genuinely unexpected fault log loudly
      // instead of blanking the app.
      console.error('[chart] applyChartSource failed; leaving the base as-is', err);
    }
  }, []);

  // Re-apply the chart source when the provider / override / satellite settings
  // change (a genuine source change, not a pan).
  useEffect(() => {
    applyChartSource('source');
  }, [applyChartSource, chartProviderId, chartOverrideId, bsbCharts, packCharts, imageryDim, satelliteApiKey, satEnabled, satSourceId, satOpacity]);

  // OpenSeaMap seamark OVERLAY. Re-seats the marks above the base and below our own
  // overlays. Imperative + store-read so it can also be called after a palette
  // setStyle (which wipes it) without depending on a per-move React tick.
  const applySeamark = useCallback(() => {
    try {
      const map = mapRef.current;
      if (!map || !isStyleReady(map)) return;
      const providerId = useChartPlotterStore.getState().chartProviderId;
      const on = useSeamarkStore.getState().enabled || getProvider(providerId).impliesSeamarks === true;
      setSeamarkOverlay(map, on, seamarkTilesTemplate());
    } catch (err) {
      console.error('[chart] applySeamark failed', err);
    }
  }, []);
  useEffect(() => {
    applySeamark();
  }, [applySeamark, seamarkOn, chartProviderId, chartOverrideId, bsbCharts, packCharts, satEnabled, satSourceId]);

  // Real-world wind refetch over the current extent, imperative so a pan doesn't
  // re-render this component. bsb + a real weather source + weather overlay only.
  const loadRealWeatherForView = useCallback(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    const env = useEnvStore.getState();
    // NOT gated on Plotting any more. The weather overlay is something a skipper
    // puts up while under way, and requiring Plotting mode meant turning it on in
    // Live fetched nothing and drew nothing, which reads as broken (Edd,
    // fb-msgf00tl-1: "the weather overlay isn't showing").
    const showW = env.overlayMode === 'weather';
    const providerId = useChartPlotterStore.getState().chartProviderId;
    if (!showW || env.weatherSource === 'synthetic' || !providerHasRealWeather(providerId)) return;
    const b = map.getBounds();
    // Fetch well beyond the viewport, on purpose. Fetching the bare view meant
    // every pan and every zoom-out left the screen partly (or wholly) outside
    // the loaded field, so the wash and the arrows stopped at an invisible wall
    // and a refetch fired on every gesture (fb-msie7e90-0: "the wind stopped
    // showing properly when I zoomed out", plus a burst of requests the weather
    // server is entitled to start refusing). Sixty percent padding each side
    // covers a full zoom-out step and ordinary panning, and the stores skip the
    // refetch entirely while the view stays inside what is already loaded.
    const padLon = (b.getEast() - b.getWest()) * 0.6;
    const padLat = (b.getNorth() - b.getSouth()) * 0.6;
    const bounds: [number, number, number, number] = [
      b.getWest() - padLon,
      Math.max(-85, b.getSouth() - padLat),
      b.getEast() + padLon,
      Math.min(85, b.getNorth() + padLat),
    ];
    const targetMs = Date.now() + env.weatherForecastH * 3_600_000;
    // Both fetches cover the same span, so the arrows and the wash never disagree
    // about how far the time bar can go.
    const days = Math.max(1, Math.round(maxForecastH(env.weatherSource) / 24));
    useRealWeatherStore.getState().load(bounds, env.weatherSource, targetMs, days);
    // The wash is a separate request on purpose: one variable per request, so a
    // model with no gust output costs us the gusts and not the wind as well.
    // Waves go further: their own model entirely, from the wave picker, because
    // the wave models are a different catalogue on a different endpoint.
    const overlay = useWeatherOverlayStore.getState();
    const varId = overlay.variableId;
    const fieldModel = varId === 'waves' ? overlay.waveModelId : env.weatherSource;
    const fieldDays = Math.max(1, Math.round(maxForecastH(fieldModel) / 24));
    useWeatherFieldStore.getState().load(bounds, fieldModel, varId, fieldDays);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    // Self-heal: if the initial overlay mount was interrupted (e.g. a chart cell
    // threw during load), the ownship source may never have been created, and
    // updateOwnshipLayer below would no-op forever — so the vessel marker never
    // draws even though tracking still centres the map on the fix. If the style
    // is ready but the source is missing, mount the marker (and its icon) now, so
    // the boat always appears once we have a position, independent of the charts.
    try {
      const ui = getPalette(palette);
      // The boat marker is a symbol layer using the 'plot-boat' image. On the tiles
      // provider (openseamap) a memory-pressured WebGL context loss can drop that
      // registered image while the source and layer survive, so the boat feature is
      // present but draws nothing. Re-register the icon if it went missing, and
      // rebuild the source/layer if THAT was dropped, so the boat always shows once
      // we have a fix, on any provider.
      if (!map.hasImage('plot-boat')) registerPlotIcons(map, ui.UI_ACCENT, ui.CHBLK);
      if (!map.getSource('ownship') || !map.getLayer('ownship-boat')) {
        registerPlotIcons(map, ui.UI_ACCENT, ui.CHBLK);
        ensureOwnshipLayer(map, ownship, tracks, palette, ownVectorMin, fixStale, currentOwnVectors());
      }
      updateOwnshipLayer(map, ownship, withSavedTracks(tracks, savedTracks, savedHiddenIds), ownVectorMin, fixStale, currentOwnVectors());
    } catch (err) {
      console.error('[chart] ownship layer update failed', err);
    }
    // The wind vector is a barb drawn in nautical miles, so it has to rebuild as
    // the map zooms to hold a sensible on-screen size (the same reason the weather
    // barbs rebuild on zoom). Cheap: one boat, rebuilt only while actively zooming.
    const onZoomOwnship = () => {
      if (!isStyleReady(map)) return;
      try {
        updateOwnshipLayer(map, ownship, withSavedTracks(tracks, savedTracks, savedHiddenIds), ownVectorMin, fixStale, currentOwnVectors());
      } catch (err) {
        console.error('[chart] ownship zoom rebuild failed', err);
      }
    };
    map.on('zoom', onZoomOwnship);
    return () => {
      map.off('zoom', onZoomOwnship);
    };
  }, [ownship, tracks, savedTracks, savedHiddenIds, palette, ownVectorMin, fixStale, vCogsog, vHeading, vWind, vTide, positionSourceKind, streamTimeMs, weatherSource]);

  // Safety-depth declutter: record the boat's safety depth (read into the hazard
  // layer filters at build time) and re-apply those filters live when it changes,
  // so wrecks/rocks deeper than it drop out at low zoom. Runs on mount before the
  // async cell load, so the first build picks the value up too.
  useEffect(() => {
    setDeclutterSafetyDepth(safetyDepthM);
    const map = mapRef.current;
    if (map && isStyleReady(map)) applyHazardDeclutter(map);
  }, [safetyDepthM]);

  // Depth-area shading contours: record the user's shallow/deep transitions and
  // re-bin the depth-area band filters live when they change.
  useEffect(() => {
    setDepthShading(shadingShallowM, shadingDeepM);
    setDepthShadingMode(depthShadingMode);
    const map = mapRef.current;
    if (map && isStyleReady(map)) applyDepthBands(map);
  }, [shadingShallowM, shadingDeepM, depthShadingMode]);

  // Declutter level: record the medium-level marker (read into the buoy/beacon
  // filters at build time) so the first cell build picks it up, and re-apply live
  // when it changes. The visibility-based hides (spot soundings, light flares)
  // ride the category/visibility effect below; here we just re-bin the mark
  // filters (Medium shows only the significant buoys) and re-run visibility so a
  // level change with unchanged categories still refreshes the hides.
  useEffect(() => {
    setDeclutterLevel(declutterLevel);
    const map = mapRef.current;
    if (map && isStyleReady(map)) {
      applyVisibility(map, visibility);
      applyDeclutterMarks(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declutterLevel]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    // Self-heal: ensure* recreates the source/layers if a style or WebGL-context
    // disruption dropped them, then setData. update* alone no-ops when the source
    // is missing, which is how an in-progress overlay could vanish for good on a pan.
    try {
      ensureObjectsLayer(map, objectsOrder.map((id) => objectsById[id]).filter(Boolean), palette);
    } catch (err) {
      console.error('[chart] objects layer update failed', err);
    }
  }, [objectsById, objectsOrder, palette]);


  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    // While following, redraw the active-leg line (boat -> next waypoint) as the
    // boat moves, so it tracks own ship live.
    const follow =
      followRouteId && ownship.position
        ? {
            routeId: followRouteId,
            boat: ownship.position,
            fromLegIndex: followFromLegIndex ?? 0,
            legIndex: activeLegIndex,
          }
        : null;
    // Hidden routes are dropped from the chart; the followed route always stays
    // shown so its active-leg line never disappears.
    const visibleRoutes = routes.filter((r) => !hiddenRouteIds.includes(r.id) || r.id === followRouteId);
    // Warning hatch for the last route check, but only while its route is still
    // shown. Reduce the per-leg findings to what the layer needs: hazard vs caution.
    let hazards: RouteHazardHighlight | null = null;
    if (routeSafetyCheck && visibleRoutes.some((r) => r.id === routeSafetyCheck.routeId)) {
      const legs = routeSafetyCheck.result.legs
        .map((l) => ({ legIndex: l.legIndex, severity: legHighlightSeverity(l) }))
        .filter((l): l is { legIndex: number; severity: NonNullable<typeof l.severity> } => l.severity !== null);
      if (legs.length) hazards = { routeId: routeSafetyCheck.routeId, legs };
    }
    try {
      ensureRouteLayer(map, visibleRoutes, activeRouteId, palette, follow, editingLegXtd, hazards);
    } catch (err) {
      console.error('[chart] route layer update failed', err);
    }
  }, [routes, activeRouteId, followRouteId, followFromLegIndex, activeLegIndex, hiddenRouteIds, ownship.position, editingLegXtd, palette, routeSafetyCheck]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    let gotoLeg: GotoLeg | null = null;
    if (goto && ownship.position) {
      const mark = marks.find((m) => m.id === goto.markId);
      if (mark) gotoLeg = { boat: ownship.position, target: mark.position, targetName: mark.name };
    }
    try {
      ensureMarksLayer(map, marks, palette, gotoLeg);
    } catch (err) {
      console.error('[chart] marks layer update failed', err);
    }
  }, [marks, goto, ownship.position, palette]);

  // Keep the on-chart notes in step with the store: a placed, edited or deleted
  // note redraws here, and ensureNotesLayer re-seats them above the vector cells.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    try {
      ensureNotesLayer(map, notes, palette);
    } catch (err) {
      console.error('[chart] notes layer update failed', err);
    }
  }, [notes, palette]);

  // Keep notes on top the same way ownship and marks stay on top: re-raise them
  // as the boat position ticks and whenever the provider or satellite overlay
  // changes, so async-loaded vector cells or a provider swap never bury a note.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    try {
      if (map.getLayer('notes-icon')) raiseNotesLayers(map);
      // Same treatment for the AIS targets: a provider switch or the boat tick
      // can re-stack cell fills over them, so climb them back above the chart.
      if (map.getLayer('ais-tri')) raiseAisLayers(map);
      // And the pilotage plan: it has no anchor of its own in the cell stack, so a
      // provider swap or the boat tick can bury it behind the vector fills too.
      if (map.getLayer('plan-line-route')) raisePilotagePlanLayers(map);
      // The boat and the anchor swing circle were missing from this list, and
      // they are the two things a skipper at anchor is actually looking for
      // (fb-msie4sj8-0: neither drew at z17, because the on-demand band-5 cell
      // had appended its fills above them and nothing ever raised them back).
      // The circle goes first so the boat stays on top of its own ring.
      if (map.getLayer('anchor-swing-fill') || map.getLayer('anchor-hook')) raiseAnchorWatchLayers(map);
      // The pilotage tools were the last overlay without this defence, and a
      // transit plotted over freshly loaded NOAA cells vanished under their
      // fills the moment it was made (fb-msn947se-3).
      if (map.getLayer('pilotage-line') || map.getLayer('pilotage-transit-line')) raisePilotageLayers(map);
      if (map.getLayer('ownship-boat')) raiseOwnshipLayers(map);
    } catch (err) {
      console.error('[chart] notes raise failed', err);
    }
  }, [ownship.position, chartProviderId, satEnabled]);

  // Drag-a-box area selection for chart downloads (fb-msn0j0jv-3). Armed from
  // the packs panel, the same arm-and-watch pattern as placing the anchor: while
  // armed, the next press-drag draws a rectangle and releasing stages the packs
  // it covers. Pan is disabled for the duration, so the gesture cannot fight
  // the map, and the effect's cleanup puts everything back however it ends.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !areaSelectArmed || !isStyleReady(map)) return;
    const SRC = 'area-select';
    let start: [number, number] | null = null;
    const boxOf = (a: [number, number], b: [number, number]): [number, number, number, number] => [
      Math.min(a[0], b[0]),
      Math.min(a[1], b[1]),
      Math.max(a[0], b[0]),
      Math.max(a[1], b[1]),
    ];
    const ringOf = (bx: [number, number, number, number]) => [
      [bx[0], bx[1]],
      [bx[2], bx[1]],
      [bx[2], bx[3]],
      [bx[0], bx[3]],
      [bx[0], bx[1]],
    ];
    const draw = (bx: [number, number, number, number] | null) => {
      const fc = {
        type: 'FeatureCollection' as const,
        features: bx
          ? [{ type: 'Feature' as const, geometry: { type: 'Polygon' as const, coordinates: [ringOf(bx)] }, properties: {} }]
          : [],
      };
      const src = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
      if (src) src.setData(fc);
    };
    try {
      if (!map.getSource(SRC)) map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      if (!map.getLayer('area-select-fill'))
        map.addLayer({ id: 'area-select-fill', type: 'fill', source: SRC, paint: { 'fill-color': '#cc1f8f', 'fill-opacity': 0.12 } });
      if (!map.getLayer('area-select-line'))
        map.addLayer({ id: 'area-select-line', type: 'line', source: SRC, paint: { 'line-color': '#cc1f8f', 'line-width': 2, 'line-dasharray': [3, 2] } });
    } catch {
      /* a mid-styleswap arm just retries on the next render */
    }
    map.dragPan.disable();
    map.getCanvas().style.cursor = 'crosshair';
    const onDown = (e: MapMouseEvent | MapTouchEvent) => {
      start = [e.lngLat.lng, e.lngLat.lat];
      e.preventDefault();
    };
    const onMove = (e: MapMouseEvent | MapTouchEvent) => {
      if (!start) return;
      draw(boxOf(start, [e.lngLat.lng, e.lngLat.lat]));
    };
    const onUp = (e: MapMouseEvent | MapTouchEvent) => {
      if (!start) return;
      const bx = boxOf(start, [e.lngLat.lng, e.lngLat.lat]);
      start = null;
      // A tap is not an area — it is "never mind". Cancelling on a tap also
      // means an accidentally armed select can always be escaped by touching
      // the chart, rather than leaving a map that will not pan and offers no
      // obvious way out (the likely shape of fb-msnbeubc-1, "frozen, can't
      // scroll").
      if (Math.abs(bx[2] - bx[0]) < 0.005 && Math.abs(bx[3] - bx[1]) < 0.005) {
        draw(null);
        useChartPacksStore.getState().cancelAreaSelect();
        useShellStore.getState().showToast('Area choosing cancelled');
        return;
      }
      useChartPacksStore.getState().stageBox(bx);
    };
    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    map.on('touchstart', onDown);
    map.on('touchmove', onMove);
    map.on('touchend', onUp);
    map.on('touchcancel', onUp);
    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.off('touchstart', onDown);
      map.off('touchmove', onMove);
      map.off('touchend', onUp);
      map.off('touchcancel', onUp);
      try {
        if (map.getLayer('area-select-fill')) map.removeLayer('area-select-fill');
        if (map.getLayer('area-select-line')) map.removeLayer('area-select-line');
        if (map.getSource(SRC)) map.removeSource(SRC);
      } catch {
        /* teardown race */
      }
      map.getCanvas().style.cursor = '';
      map.dragPan.enable();
    };
  }, [areaSelectArmed]);

  // Ground-vector colour: the skipper's pick, or the palette accent when unset.
  // Runs on palette change too, because the accent is a different colour there
  // and "Theme" means follow it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    applyOwnVectorColour(map, ownVectorColour, getPalette(palette).UI_ACCENT);
  }, [ownVectorColour, palette, ownship.position]);

  // The passage destination is a DOM marker (see destinationLayer.ts), so it is
  // immune to the GeoJSON worker-tiling drop that left a source-based marker blank.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    setDestinationMarker(map, destination, () => useShellStore.getState().openSheet('destination'));
  }, [destination]);

  // Scan the planned passage route for charted hazards near its track, for the
  // assisted plan's "check" step. Recomputed as the route is shaped.
  useEffect(() => {
    const route = routes.find((r) => r.id === plannedRouteId);
    if (!route || route.waypoints.length < 2) {
      useRoutePlanStore.getState().setRouteHazards([]);
      useRoutePlanStore.getState().setPilotageSections([]);
      return;
    }
    const index = new ChartIndex(Array.from(cellsRef.current.values()));
    const wps = route.waypoints.map((w) => w.position);
    // The depth-based hazard scan can only read a vector ENC; on a raster/tiles
    // source it has nothing to check, so clear it and let the plan step report
    // the unavailable state rather than implying "no hazards found".
    const scanHasDepth = providerHasDepthData(useChartPlotterStore.getState().chartProviderId);
    const hazards = scanHasDepth ? scanRouteHazards(wps, index, safetySettings, 0.5) : [];
    useRoutePlanStore.getState().setRouteHazards(hazards);
    const hazardLegs = [...new Set(hazards.map((h) => h.legIndex))];
    const draught = useSettingsStore.getState().draughtM;
    const sections = detectPilotageSections(wps, {
      cells: index,
      hazardLegIndices: hazardLegs,
      shoalDepthM: draught + 5,
    });
    useRoutePlanStore.getState().setPilotageSections(sections);
  }, [plannedRouteId, routes, safetySettings]);

  // Fly the chart to a focus request (e.g. centring on a pilotage section).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    map.flyTo({ center: focus.center, zoom: focus.zoom, duration: 600 });
  }, [focus]);

  // Fit the chart to a bounds request (e.g. loading a pilotage plan so the whole
  // plan sits in view). Mirrors the training-cell fit; the nonce lets a repeat
  // fit to the same bounds fire again.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fit) return;
    const [minLon, minLat, maxLon, maxLat] = fit.bounds;
    map.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding: 64, duration: 600, maxZoom: 15.5 },
    );
  }, [fit]);

  // Run a requested auto-route against the chart cells (which live here). The cost
  // is built from the charted depths, the tide diamonds and the wind/polar; the
  // result is a SUGGESTED route, gated as such in the UI. We plot it whatever the
  // depths look like (charted shallow no longer refuses), then run the tide-aware
  // safety check and surface the limitations, so the crew always gets a route
  // plus a clear read of the shallow legs and their tidal gates.
  useEffect(() => {
    if (!autoRequest) return;
    const cells = Array.from(cellsRef.current.values());
    const st = useSettingsStore.getState();
    const env = useEnvStore.getState();
    // On a real-area chart with a real source loaded, route on the cached real
    // wind FIELD, reading the wind at the time the boat reaches each point. Real
    // wind is wall-clock + the chosen forecast offset (the 2019 training scrubber
    // doesn't apply), and there's no real tide model for the real area yet, so
    // tide is zero there. (This branch needs charted depths, which the raster
    // Antares charts don't carry, so it stays ready for a real vector chart.)
    const rw = useRealWeatherStore.getState();
    const realRoute =
      env.weatherSource !== 'synthetic' &&
      providerHasRealWeather(useChartPlotterStore.getState().chartProviderId) &&
      rw.field !== null;
    const realDepartMs = Date.now() + env.weatherForecastH * 3_600_000;
    const realField = rw.field;
    const departMs = realRoute ? realDepartMs : env.streamTimeMs;
    // Height of tide to add to charted depths for the router's navigability check,
    // so a spot that's charted-shallow but floods enough isn't wrongly walled off.
    // A single representative height at the area's midpoint is plenty for a coarse
    // suggested route; the per-leg tidal gate below does the honest, point-by-point
    // work. Training area only (the real raster charts carry no depths or tide).
    let navTideM = 0;
    if (!realRoute && providerHasDepthData(useChartPlotterStore.getState().chartProviderId)) {
      const mid: [number, number] = [
        (autoRequest.start[0] + autoRequest.destination[0]) / 2,
        (autoRequest.start[1] + autoRequest.destination[1]) / 2,
      ];
      const near = nearestHeightPort(mid);
      if (near && near.distNm <= 60) {
        const h = heightAt(near.port, departMs);
        if (h != null && Number.isFinite(h)) navTideM = h;
      }
    }
    const out = planAutoRoute({
      start: autoRequest.start,
      destination: autoRequest.destination,
      departMs,
      cells,
      draughtM: st.draughtM,
      clearanceM: 0.5,
      cruiseKn: st.cruiseSpeedKn,
      tideHeightM: navTideM,
      polar: st.polarEnabled ? st.polar : null,
      spacingNm: 0.4,
      tideFn: realRoute
        ? () => ({ setDeg: 0, rateKn: 0 })
        : (pos, ms) => streamAtPosition(pos, ms),
      windFn:
        realRoute && realField
          ? (pos, ms) => windAtTime(realField, pos, ms)
          : (pos, ms) => {
              const w = windAt(env.weatherSource, ms, pos);
              return { dirFromDeg: w.dirFromDeg, speedKn: w.speedKn };
            },
    });
    // planAutoRoute plots and warns rather than refusing, so an 'error' is now only
    // a genuinely degenerate case; keep the guard defensive.
    if ('error' in out) {
      useRoutePlanStore.getState().finishAuto(out.error);
      useShellStore.getState().showToast(out.error);
      return;
    }
    const id = nextRouteIdFn();
    const waypoints = out.waypoints.map((pos, i) => ({
      id: nextWaypointId(),
      position: pos as [number, number],
      name: i === 0 ? 'Start' : i === out.waypoints.length - 1 ? 'Destination' : `WP${i}`,
    }));
    const rs = useRoutingStore.getState();
    rs.upsertRoute({ id, name: 'Auto route', waypoints });
    rs.setActiveRoute(id);
    const rp = useRoutePlanStore.getState();
    rp.setPlannedRouteId(id);
    rp.finishAuto();

    // Run the tide-aware safety check over the freshly plotted route and store it,
    // so both the Route sheet panel and the chart highlight spell out the shallow
    // legs: passable within a tidal gate, a genuine hazard, or needing a manual
    // tide calc. Reuse the live chart probe (same land/depth/tide samplers).
    const probe = getChartProbe();
    let toast: string;
    if (probe) {
      const result = checkRouteSafety({
        waypoints: waypoints.map((w) => w.position),
        safetyDepthM: st.safetyDepthM,
        landRingsForLeg: probe.landRingsForLeg,
        depthAt: probe.depthAt,
        inCoverage: probe.inCoverage,
        tideHeightAt: probe.tideHeightAt,
        assessFromMs: departMs,
        assessWindowHours: 24,
      });
      useRouteSafetyStore.getState().setCheck(id, result);
      const flagged = result.hazardCount + result.gatedCount + result.manualTideCount + result.cautionCount;
      if (out.limited) {
        toast = `Auto-route plotted (direct line) — ${summariseRouteSafety(result).headline}`;
      } else if (flagged > 0) {
        toast = `Auto-route plotted — ${summariseRouteSafety(result).headline}`;
      } else {
        toast = 'Auto-route plotted — suggested, check at high zoom';
      }
    } else {
      toast = out.limited
        ? 'Auto-route plotted (direct line) — run Check route once the chart finishes loading'
        : 'Auto-route plotted — suggested, check at high zoom';
    }
    useShellStore.getState().showToast(toast);
  }, [autoRequest]);

  // Redraw clearing bearings when they change, the selection changes, a plan is
  // hidden/shown, or the tripped set changes (so the crossed line goes red).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    const visible = clearings.filter((c) => c.planId == null || !hiddenPlanIds.includes(c.planId));
    const visibleTransits = transits.filter((t) => t.planId == null || !hiddenPlanIds.includes(t.planId));
    const visibleHeadings = headingBearings.filter(
      (h) => h.planId == null || !hiddenPlanIds.includes(h.planId),
    );
    const tripped = new Set((alarmClearings ?? []).map((c) => c.id));
    try {
      ensurePilotageLayer(
        map,
        visible,
        visibleTransits,
        visibleHeadings,
        activeClearingId,
        activeTransitId,
        activeHeadingId,
        tripped,
        palette,
      );
    } catch (err) {
      console.error('[chart] pilotage layer update failed', err);
    }
  }, [
    clearings,
    transits,
    headingBearings,
    activeClearingId,
    activeTransitId,
    activeHeadingId,
    hiddenPlanIds,
    alarmClearings,
    palette,
  ]);


  useEffect(() => {
    const setRec = useShellStore.getState().setLegXtdRecommendation;
    if (!editingLegXtd) {
      setRec(null);
      return;
    }
    const route = useRoutingStore.getState().routes.find((r) => r.id === editingLegXtd.routeId);
    const i = route?.waypoints.findIndex((w) => w.id === editingLegXtd.fromId) ?? -1;
    if (!route || i < 0 || i + 1 >= route.waypoints.length) {
      setRec(null);
      return;
    }
    setRec(
      recommendXtdForLeg(route.waypoints[i].position, route.waypoints[i + 1].position, chartIndexNow(), safetySettings),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingLegXtd]);

  // Recompute dangers ahead whenever ownship state or settings change.
  useEffect(() => {
    if (!ownship.position || ownship.sog <= 0) {
      if (dangers.length) setDangers([]);
      return;
    }
    const hits = dangersAhead(
      { position: ownship.position, cog: ownship.cog || ownship.heading, sog: ownship.sog },
      chartIndexNow(),
      safetySettings,
    );
    if (
      hits.length !== dangers.length ||
      hits.some((h, i) => h.feature.properties.id !== dangers[i]?.feature.properties.id)
    ) {
      setDangers(hits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownship.position, ownship.cog, ownship.heading, ownship.sog, safetySettings]);

  const aisTargets = useAisStore((s) => s.targets);
  const aisEnabled = useAisStore((s) => s.enabled);
  const aisSelected = useAisStore((s) => s.selectedMmsi);
  const aisVectorMin = useAisStore((s) => s.vectorMin);

    // Underway alarms (charted-hazard proximity, charted depth under the boat).
  // Computed here because both the chart cells and the live position are to
  // hand; the result is pushed to the alarms store and rendered by the banner,
  // so warnings are raised in one place. Runs whether or not we're moving (a
  // depth or a danger matters at anchor and adrift, not only under way).
  useEffect(() => {
    const pos = ownship.position;
    if (!pos) {
      setAlarmsLive({ proximity: null, depth: null, clearing: null, clearingDepth: null, anchor: null, grounding: null, tideHeight: null, ais: { proximity: null, cpa: null }, waypointArrival: null, offTrack: null, gnssAccuracy: useAlarmsStore.getState().live.gnssAccuracy, gnssLost: useAlarmsStore.getState().live.gnssLost, gnssJump: useAlarmsStore.getState().live.gnssJump, lowBattery: useAlarmsStore.getState().live.lowBattery });
      return;
    }
    const cells = chartIndexNow();
    // Depth only exists in the vector ENC. On a raster/tiles source there's
    // nothing to read, so we don't quietly read a hidden training cell that
    // doesn't match the chart on screen — we treat depth as unavailable and let
    // the UI say so. The charted depth under the boat is wanted by both the depth
    // alarm and the clearing-depth guards, so compute it once.
    const hasDepthData = providerHasDepthData(useChartPlotterStore.getState().chartProviderId);
    const chartedM = hasDepthData ? chartedDepthUnderBoat(pos, cells) : null;
    // Height of tide now (same scrubber the card + bridge use) -> depth under the
    // keel = charted depth + tide - draught. Published so data fields can read it.
    // Only where the training model actually covers the water. On a real chart the
    // Namley ports say nothing, and a fictional height added to a real charted
    // depth would drive the depth alarm off an invented number. Null here means
    // chart datum, which is the cautious reading.
    const tideNowM = heightNowM({
      providerId: useChartPlotterStore.getState().chartProviderId,
      heightSourceId: useTideSourceStore.getState().heightSourceId,
      portId: heightPortId,
      ms: streamTimeMs,
    });
    const underKeelM = depthUnderKeel(chartedM, tideNowM, boatDraughtM);
    useGnssStore.getState().setChartedDepthUnderBoat(chartedM);
    let proximity: AlarmsLive['proximity'] = null;
    if (alarmsConfig.proximityEnabled) {
      const hits = dangersWithin(pos, cells, safetySettings, alarmsConfig.proximityNm);
      if (hits.length) {
        proximity = { label: HAZARD_LABEL[hits[0].reason], rangeNm: hits[0].rangeNm };
      }
    }
    let depth: AlarmsLive['depth'] = null;
    if (alarmsConfig.depthEnabled) {
      if (chartedM != null && underKeelM != null && underKeelM < alarmsConfig.depthLimitM) {
        depth = {
          underKeelM,
          chartedM,
          tideM: tideNowM,
          draughtM: boatDraughtM,
          limitM: alarmsConfig.depthLimitM,
        };
      }
    }
    let clearing: AlarmsLive['clearing'] = null;
    if (alarmsConfig.clearingEnabled) {
      const tripped = clearings.filter((c) => c.alarm && isClearingTripped(c, pos));
      if (tripped.length) clearing = tripped.map((c) => ({ id: c.id, label: clearingDisplayName(c) }));
    }
    let clearingDepth: AlarmsLive['clearingDepth'] = null;
    if (alarmsConfig.clearingDepthEnabled) {
      // Position/charted crossing now; the measured echo-sounder depth (DPT/DBT)
      // also trips it when real hardware is feeding it (null until then).
      const measuredM = useGnssStore.getState().measuredDepthM;
      const active = clearingDepths.filter(
        (d) => d.alarm && (d.planId == null || !hiddenPlanIds.includes(d.planId)),
      );
      const tripped = active.filter(
        (d) =>
          isClearingDepthTripped(d, underKeelM ?? chartedM) ||
          (measuredM != null && (d.side === 'deep' ? measuredM >= d.valueM : measuredM <= d.valueM)),
      );
      if (tripped.length) {
        clearingDepth = tripped.map((d) => ({
          id: d.id,
          label: clearingDepthDisplayName(d),
          valueM: d.valueM,
          chartedM: underKeelM ?? chartedM ?? measuredM ?? d.valueM,
        }));
      }
    }
    // Anchor watch: if the hook is down, record the swing and flag a drag once the
    // boat sits beyond the swing circle plus the GPS-wander buffer.
    let anchor: AlarmsLive['anchor'] = null;
    const aw = useAnchorWatchStore.getState();
    if (aw.anchor) {
      aw.pushSwing(pos);
      const radiusNm = swingRadiusNm({ scopeM: aw.scopeM, depthM: aw.depthM, bowHeightM: aw.bowHeightM });
      const dc = dragCheck(aw.anchor, pos, radiusNm, aw.bufferM / 1852);
      // In custom-zone mode the boundary is the drawn polygon, not the swing
      // circle; in radius mode it's the circle plus the GPS-wander buffer. Either
      // way it lands in the same live alarm so the siren, haptics and background
      // path treat an anchor drag identically.
      if (aw.mode === 'zone' && aw.zone.length >= 3) {
        if (outsideZone(aw.zone, pos)) anchor = { distanceNm: dc.distanceNm, radiusNm: 0, mode: 'zone' };
      } else if (dc.dragging) {
        anchor = { distanceNm: dc.distanceNm, radiusNm, mode: 'radius' };
      }
    }
    // Grounding: project the straight track ahead against charted / safety depth.
    let grounding: AlarmsLive['grounding'] = null;
    if (alarmsConfig.groundingEnabled && hasDepthData) {
      // Look as far ahead as the danger cone reaches, so the two agree: in time
      // mode that's the set minutes; in distance mode we convert the fixed reach
      // back to minutes at the current SOG (projectGrounding walks forward in
      // time). A near-stop makes way too slowly for a meaningful projection, so
      // we fall back to the time-mode minutes there.
      const horizonMin =
        safetySettings.lookaheadMode === 'distance' && ownship.sog > 0.1
          ? (lookaheadDistanceNm(ownship.sog, safetySettings) / ownship.sog) * 60
          : safetySettings.lookaheadMin;
      const g = projectGrounding({
        position: pos,
        cogDeg: ownship.cog || ownship.heading,
        sogKn: ownship.sog,
        depthAt: (p: [number, number]) => chartedDepthUnderBoat(p, cells, 0.04),
        safetyDepthM: safetySettings.depthM,
        draughtM: boatDraughtM,
        tideM: tideNowM ?? undefined,
        horizonMin,
      });
      if (g.willGround && g.etaMin != null) {
        grounding = { etaMin: g.etaMin, distanceNm: g.distanceNm ?? 0, clearanceM: g.clearanceM };
      }
    }
    // Tide-height alarm: warn when the height of tide now crosses the set limit
    // (too low to float / clear the sill, or too high under a bridge).
    let tideHeight: AlarmsLive['tideHeight'] = null;
    if (alarmsConfig.tideHeightEnabled && tideNowM != null) {
      const breached =
        alarmsConfig.tideHeightMode === 'below'
          ? tideNowM < alarmsConfig.tideHeightLimitM
          : tideNowM > alarmsConfig.tideHeightLimitM;
      if (breached) {
        tideHeight = { heightM: tideNowM, limitM: alarmsConfig.tideHeightLimitM, mode: alarmsConfig.tideHeightMode };
      }
    }
    // Route / goto alarms: waypoint arrival inside a set radius, and running
    // beyond the followed leg's cross-track limit. Computed here so they land in
    // the same live snapshot the siren, haptics and background path read, rather
    // than only as banner strings.
    const routeAlarms = evaluateRouteAlarms({ position: pos, routes, marks, followRouteId, followFromLegIndex, activeLegIndex, activeRouteId });
    setAlarmsLive({ proximity, depth, clearing, clearingDepth, anchor, grounding, tideHeight, ais: useAlarmsStore.getState().live.ais, waypointArrival: routeAlarms.waypointArrival, offTrack: routeAlarms.offTrack, gnssAccuracy: useAlarmsStore.getState().live.gnssAccuracy, gnssLost: useAlarmsStore.getState().live.gnssLost, gnssJump: useAlarmsStore.getState().live.gnssJump, lowBattery: useAlarmsStore.getState().live.lowBattery });
    // Keep the on-chart anchor watch (boundary colour + swing track) in step with
    // the drag state we just computed, so a drag reads on the chart, not only the
    // banner.
    {
      const awMap = mapRef.current;
      if (awMap && isStyleReady(awMap)) updateAnchorWatchLayer(awMap, currentAnchorWatchInput(!!anchor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownship.position, ownship.cog, ownship.sog, ownship.heading, alarmsConfig, boatDraughtM, safetySettings, clearings, clearingDepths, hiddenPlanIds, heightPortId, streamTimeMs, routes, marks, followRouteId, followFromLegIndex, activeLegIndex]);

  // AIS layer + the AIS proximity / CPA alarms. Recomputed when targets move (the
  // sim/feed updates the store) or own ship changes. Threats (small CPA, closing)
  // colour red on the chart and raise the CPA alarm; the nearest in range raises
  // the proximity alarm. Merged into the live alarms without clobbering the rest.
  // Coalesced to ~1.4 Hz: own ship updates at the GNSS tick and a busy live feed
  // can push targets several times a second; recomputing CPA for every target and
  // rebuilding the whole AIS layer on each of those is wasteful. run() reads fresh
  // state, so a trailing (throttled) pass is never stale.
  const aisThrottle = useRef<{ last: number; timer: number | null }>({ last: 0, timer: null });
  useEffect(() => {
    const th = aisThrottle.current;
    const run = () => {
      th.last = Date.now();
      const map = mapRef.current;
      if (!map || !map.getLayer('ais-tri')) return;
      const ais = useAisStore.getState();
      const cfg = useAlarmsStore.getState().config;
      const os = useChartPlotterStore.getState().ownship;
      const nowMs = Date.now();
      // Drop targets we haven't heard from in a very long time (both sim and live
      // feeds), so ghosts don't pile up in the store, and keep them out of this
      // frame too.
      ais.prune(nowMs - AGE_DROP_MS);
      const targets = Object.values(ais.targets).filter((t) => nowMs - t.lastMs < AGE_DROP_MS);
      const threats = new Set<number>();
      let proximity: { label: string; rangeNm: number } | null = null;
      let cpa: { label: string; cpaNm: number; tcpaMin: number } | null = null;
      if (os.position && ais.enabled) {
        const ownK = { position: os.position, cogDeg: os.cog || os.heading, sogKn: os.sog };
        for (const t of targets) {
          // A lost target's position/course are stale guesses; don't let a ghost
          // raise a CPA or proximity alarm.
          if (isLost(t, nowMs)) continue;
          const r = computeCpa(ownK, { position: t.position, cogDeg: t.cogDeg, sogKn: t.sogKn });
          const label = t.name || String(t.mmsi);
          if (cfg.aisProximityEnabled && r.rangeNm <= cfg.aisProximityNm) {
            if (!proximity || r.rangeNm < proximity.rangeNm) proximity = { label, rangeNm: r.rangeNm };
          }
          if (cfg.aisCpaEnabled && r.tcpaMin > 0 && r.tcpaMin <= cfg.aisTcpaMin && r.cpaNm <= cfg.aisCpaNm) {
            threats.add(t.mmsi);
            if (!cpa || r.tcpaMin < cpa.tcpaMin) cpa = { label, cpaNm: r.cpaNm, tcpaMin: r.tcpaMin };
          }
          // Close-quarters danger: colour red whenever the predicted CPA is under
          // half a mile and we're not already opening away, whatever the alarm
          // settings say. This only tints the target (already excludes lost ones
          // above); the audible CPA alarm still follows the config block above.
          if (r.tcpaMin >= 0 && r.cpaNm < AIS_DANGER_CPA_NM) threats.add(t.mmsi);
        }
      }
      // Clip each target's course/speed vector at the coast: a boat won't steam
      // across land, so a predictor that crosses a headland is just noise. Pull the
      // charted land polygons near each vector from the spatial index and stop the
      // line at the first crossing. No land loaded here means no clipping.
      const landIdx = chartIndexNow();
      const landClip = (from: LngLat, to: LngLat): LngLat | null => {
        const rings: LngLat[][] = [];
        for (const feat of landIdx.search(legBBox(from, to, 0.02))) {
          const isLand = feat.properties.group === 'land' || feat.properties.objClass === 'LNDARE';
          if (!isLand) continue;
          const g = feat.geometry;
          if (g.type === 'Polygon') {
            rings.push(g.coordinates[0] as LngLat[]);
          } else if (g.type === 'MultiPolygon') {
            for (const poly of g.coordinates) rings.push(poly[0] as LngLat[]);
          }
        }
        return rings.length ? clipVectorToLand(from, to, rings) : to;
      };
      updateAisLayer(map, targets, threats, ais.selectedMmsi, ais.vectorMin, nowMs, landClip);
      setAisLayersVisible(map, ais.enabled);
      setAlarmsLive({ ...useAlarmsStore.getState().live, ais: { proximity, cpa } });
    };
    const MIN_MS = 700;
    const since = Date.now() - th.last;
    if (since >= MIN_MS) {
      run();
    } else if (th.timer == null) {
      th.timer = window.setTimeout(() => {
        th.timer = null;
        run();
      }, MIN_MS - since);
    }
    return () => {
      if (th.timer != null) {
        window.clearTimeout(th.timer);
        th.timer = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aisTargets, aisEnabled, aisSelected, aisVectorMin, ownship.position, ownship.cog, ownship.heading, ownship.sog, alarmsConfig]);

  // Redden the chosen do-not-cross isobath(s) when clearing depths change or a
  // plan is shown/hidden. (Initial paint + post-restyle re-add happen in the
  // load and palette/symbol re-style handlers.)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    const vals = clearingDepths
      .filter((d) => d.planId == null || !hiddenPlanIds.includes(d.planId))
      .map((d) => d.valueM);
    updateClearingDepthHighlight(map, vals, palette);
  }, [clearingDepths, hiddenPlanIds, palette]);

  // Emphasise the highlighted depth contour(s) with an accent halo whenever the
  // set changes. (Initial paint + post-restyle re-add ride along in the load and
  // palette/symbol re-style handlers, next to the clearing-depth highlight.)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateContourHighlight(map, highlightedContourValues, palette);
  }, [highlightedContourValues, palette]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateSafetyLayer(map, { anchor, mob, dangers, ownship, settings: safetySettings });
  }, [anchor, mob, dangers, ownship, safetySettings]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateMobSearchAreaLayer(map, mobSearchOverlay);
  }, [mobSearchOverlay]);

  // Redraw the anchor-watch layer when the hook moves, the mode flips, or a zone
  // vertex is added — immediate feedback while dropping or drawing. The swing
  // track keeps updating from the per-fix alarm effect.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateAnchorWatchLayer(map, currentAnchorWatchInput(!!useAlarmsStore.getState().live.anchor));
  }, [awAnchor, awMode, awZone, awScopeM, awDepthM, awBowHeightM]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateTideLayer(map, showStream, streamArrows);
    updateWeatherLayer(map, showWeather, weatherSamples, windStyle);
    updatePinnedWindLayer(map, pinnedWind);
  }, [showStream, showWeather, streamArrows, weatherSamples, pinnedWind, windStyle]);

  // The coloured field under the symbols. Re-sliced from the cached field when the
  // hour moves, so scrubbing the time bar is a canvas redraw and not a fetch.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    if (!showWeather) {
      ensureWeatherFieldLayer(map, null, null);
      return;
    }
    const targetMs = Date.now() + weatherForecastH * 3_600_000;
    const grid = fieldGridAt({ field: wxField, variableId: wxFieldVarId }, overlayVariableId, targetMs);
    ensureWeatherFieldLayer(map, grid, fieldVariable(overlayVariableId));
    // The thresholds change where the colours sit, so the arrows' baked paint
    // expressions get pushed again and the wash re-renders through the deps.
    applyWindColourThresholds(map);
  }, [showWeather, wxField, wxFieldVarId, overlayVariableId, weatherForecastH, windGreenFromKn, windRedFromKn]);

  // Wind barbs are geometry in nautical miles, so their on-screen size changes
  // with zoom. weatherLayer scales the NM sizes by the zoom, but only when we
  // rebuild, so rebuild as the map zooms to keep the barb a sensible size rather
  // than letting it balloon on the way in. Cheap: a small grid plus one pin.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onZoom = () => {
      if (!isStyleReady(map)) return;
      updateWeatherLayer(map, showWeather, weatherSamples, windStyle);
      updatePinnedWindLayer(map, pinnedWind);
    };
    map.on('zoom', onZoom);
    return () => {
      map.off('zoom', onZoom);
    };
  }, [showWeather, weatherSamples, pinnedWind, windStyle]);

  // Range rings: redraw concentric distance rings on the boat as it moves or the
  // settings change. on=false renders nothing, so the toggle hides them cleanly.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    // Auto spacing reads the visible width in nm (from two unprojected screen
    // points) and snaps to a round interval, so the rings stay useful at any
    // zoom; manual spacing just uses the fixed intervalNm.
    const draw = () => {
      let intervalNm = rrInterval;
      if (rrAuto) {
        const canvas = map.getCanvas();
        const w = canvas.clientWidth || canvas.width;
        const h = canvas.clientHeight || canvas.height;
        const midY = h / 2;
        const left = map.unproject([0, midY]);
        const right = map.unproject([w, midY]);
        const spanNm = rhumbDistanceNm([left.lng, left.lat], [right.lng, right.lat]);
        intervalNm = autoRingIntervalNm(spanNm);
      }
      updateRangeRingsLayer(map, {
        on: rrEnabled,
        centre: ownship.position,
        intervalNm,
        count: rrCount,
        spokes: rrSpokes,
      });
    };
    draw();
    // When Auto is on, the round interval has to be recomputed as we zoom, so
    // hang a zoom listener while it's enabled and drop it otherwise.
    if (rrAuto && rrEnabled) {
      map.on('zoom', draw);
      return () => {
        map.off('zoom', draw);
      };
    }
  }, [rrEnabled, rrAuto, rrInterval, rrCount, rrSpokes, ownship]);

  // EBL / VRM: redraw the bearing line and range circle from the boat as it moves
  // or the settings change. Each is independent; off renders nothing.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateEblVrmLayer(map, {
      centre: ownship.position,
      eblOn,
      eblBearingDeg,
      vrmOn,
      vrmRangeNm,
      piOn,
      piBearingDeg,
      piOffsetNm,
    });
  }, [eblOn, eblBearingDeg, vrmOn, vrmRangeNm, piOn, piBearingDeg, piOffsetNm, ownship]);

  // Pilotage plan: redraw the whole active plan (route, bearings, contour lines,
  // freehand, notes) as it's edited, or clear it when hidden / no plan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    const doc = selectActivePlan({ plans: planPlans, activePlanId: planActiveId });
    updatePilotagePlanLayer(map, doc, planVisible, palette);
  }, [planPlans, planActiveId, planVisible, palette]);

  // Plan-building interactions: while building, plan taps/drags drive the planner
  // store (route waypoints, bearings, contours, freehand, notes). Detached when
  // not building so the normal chart handlers are back in charge.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !planBuilding) return;
    return attachPlanInteractions(map);
  }, [planBuilding]);

  // Marks & tracks visibility — driven by the Overlays show/hide toggles. The
  // user's saved data stays in place; we just hide or show its chart layers.
  const marksVisible = groupVisibility['marks'] ?? true;
  const tracksVisible = groupVisibility['track'] ?? true;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    const setVis = (ids: string[], on: boolean) => {
      for (const id of ids) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
      }
    };
    // Keep the active go-to leg visible — it's live navigation, not a static mark.
    setVis(['marks-icon', 'marks-label', 'marks-ring'], marksVisible);
    setVis(['ownship-primary-track', 'ownship-secondary-track', 'ownship-time-marks'], tracksVisible);
  }, [marksVisible, tracksVisible]);

  // Measure ruler: redraw on each added point; hidden unless the tool is active.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateMeasureLayer(map, activeTool === 'measure' ? measurePoints : []);
  }, [measurePoints, activeTool]);

  // Saved measurements: a static, dimmer overlay kept across other tools and pans.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateSavedMeasuresLayer(map, savedMeasures);
  }, [savedMeasures]);

  // Fetch real-world wind over the visible extent for a real source on a real-area chart.
  useEffect(() => {
    loadRealWeatherForView();
  }, [loadRealWeatherForView, showWeather, weatherSource, chartProviderId, weatherForecastH, overlayVariableId, overlayWaveModelId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    // Orientation (north-up / head-up / course-up) and, when the tracking lock is
    // on, keep own ship centred as the GNSS position updates. One easeTo so centre
    // and bearing move together smoothly instead of fighting each other.
    const os = ownship.position;
    const up = orientationBearingDeg(
      orientation,
      { heading: ownship.heading, cog: ownship.cog, sog: ownship.sog },
      rotation,
    );
    const bearingOff = Math.abs(((up - map.getBearing() + 540) % 360) - 180);
    if (tracking && os) {
      const bp = map.project(os);
      const cx = map.getCanvas().clientWidth / 2;
      const cy = map.getCanvas().clientHeight / 2;
      // Already centred and aligned: skip, so a stationary boat doesn't jitter.
      if (Math.hypot(bp.x - cx, bp.y - cy) < 3 && bearingOff < 0.5) return;
      map.easeTo({ center: os, bearing: up, duration: 600, easing: (t) => t });
    } else if (bearingOff > 0.5) {
      map.easeTo({ bearing: up, duration: 200 });
    }
  }, [tracking, rotation, orientation, orientNonce, ownship.heading, ownship.cog, ownship.sog, ownship.position]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    const canvas = map.getCanvas();
    canvas.style.cursor = activeTool === 'pan' ? '' : 'crosshair';
  }, [activeTool]);

  // Fly to a chart's bounds when the chart-source UI asks (so an off-screen
  // chart, e.g. a real Antares anchorage while the sim boat is elsewhere,
  // actually comes into view). Disengage the tracking lock first, otherwise the
  // camera effect would immediately yank back to the boat.
  const chartFocus = useChartSourcesStore((s) => s.chartFocus);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !chartFocus) return;
    if (useShellStore.getState().tracking) useShellStore.getState().setTracking(false);
    const [w, s, e, n] = chartFocus.bounds;
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      { padding: 48, duration: 700 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartFocus?.nonce]);

  // Recentre on the vessel when the shell button is tapped.
  const recentreRequest = useShellStore((s) => s.recentreRequest);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || recentreRequest === 0) return;
    const sh = useShellStore.getState();
    const cs = useChartPlotterStore.getState();
    const os = cs.ownship.position;
    if (!os) {
      sh.showToast('No vessel position yet');
      return;
    }
    if (!sh.tracking) {
      // Engage the tracking lock and centre; the camera effect keeps it centred
      // as the boat moves.
      sh.setTracking(true);
      map.easeTo({ center: os, duration: 400 });
      sh.showToast('Tracking on');
    } else {
      // Already tracking (so centred): the button cycles the orientation
      // north -> head -> course -> north.
      const next = nextOrientation(cs.orientation);
      cs.setOrientation(next);
      sh.showToast(orientationLabel(next));
    }
  }, [recentreRequest]);

  // Recentre on the MOB last-known position when the readout strip is tapped.
  // Mirrors the vessel recentre above (one easeTo onto the point) but never
  // engages the tracking lock, since the mark doesn't move with the boat.
  const mobRecentreRequest = useShellStore((s) => s.mobRecentreRequest);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mobRecentreRequest === 0) return;
    const mark = useSafetyStore.getState().mob;
    if (!mark) {
      useShellStore.getState().showToast('No man overboard is active');
      return;
    }
    map.easeTo({ center: mark.position, duration: 400 });
  }, [mobRecentreRequest]);

  // Swap the buoyage symbology theme live when the symbol set changes. The image
  // ids stay the same, so the buoy layers redraw with the new look without any
  // layer rebuild. Runs on mount too (sets the initial style).
  // Shade drying areas covered/uncovered for the height of tide now. Runs on the
  // time scrubber, the reference port, and any palette/set change (re-paint).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) return;
    updateDryingCover(map, dryingTideNowM(), palette, symbolSet);
  }, [streamTimeMs, heightPortId, palette, symbolSet]);

  // Symbol-set switch (Current / Paper / ECDIS). Re-skin IN PLACE — no setStyle —
  // so the per-set area/line paint and the buoy glyphs both re-apply without a
  // full reload (the bug was routing this through setStyle, where addCellToMap
  // skipped existing layers and the buoy re-skin bailed on the not-loaded guard).
  const lastSymbolSetRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleReady(map)) {
      // Same first-run trap as the palette effect: record the mounted set before the
      // style has parsed, or the ref stays null and the first REAL set switch reads as
      // the initial mount (`changed` false) — the in-place re-skin is skipped, the
      // depth-area fills keep the previous set's colours while the drying band alone
      // recolours: "why is the depth shading so broken here?".
      if (map && lastSymbolSetRef.current === null) lastSymbolSetRef.current = symbolSet;
      return;
    }
    const changed = lastSymbolSetRef.current !== null && lastSymbolSetRef.current !== symbolSet;
    lastSymbolSetRef.current = symbolSet;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = () => {
      if (changed) restyleCellsForSymbolSet(map, palette, symbolSet);
      setChartSymbolStyle(map, glyphStyleFor(symbolSet));
      updateDryingCover(map, dryingTideNowM(), palette, symbolSet);
    };
    // Poll until the style is ready rather than waiting on `map.once('idle')`:
    // the live position sim keeps the map busy, so 'idle' can be starved and the
    // re-skin would never run (the symbol set looking stuck on the previous look).
    const tryRun = () => {
      if (cancelled) return;
      // Wait for a genuinely loaded style. isStyleLoaded() === Style._loaded in this
      // MapLibre version (it is NOT gated on sources loading), so it flips true once
      // the style is parsed and stays true — safe for the setPaint/setLayout in run().
      if (isStyleReady(map)) run();
      else timer = setTimeout(tryRun, 120);
    };
    tryRun();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolSet]);

  // On-map credit. The OpenSeaMap provider shows the combined MapTiler/OSM/OpenSeaMap
  // credit; the standalone seamark overlay shows the OSM/OpenSeaMap credit.
  const activeProvider = getProvider(chartProviderId);
  const mapCredit = activeProvider.impliesSeamarks
    ? (activeProvider.attribution ?? SEAMARK_ATTRIBUTION)
    : seamarkOn
      ? SEAMARK_ATTRIBUTION
      : null;

  return (
    <div
      ref={containerRef}
      data-testid={`map-canvas-${instanceId}`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      {cells.length === 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#888',
            fontFamily: 'system-ui, sans-serif',
            pointerEvents: 'none',
          }}
        >
          Loading training cells...
        </div>
      )}
      {mapCredit && (
        <div
          style={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            padding: '2px 6px',
            fontSize: 11,
            lineHeight: 1.3,
            color: '#33475b',
            background: 'rgba(255, 255, 255, 0.78)',
            borderRadius: 4,
            fontFamily: 'system-ui, sans-serif',
            pointerEvents: 'none',
          }}
        >
          {mapCredit}
        </div>
      )}
    </div>
  );
}

const HAZARD_LABEL: Record<'wreck' | 'obstruction' | 'shallow' | 'isolated_danger', string> = {
  wreck: 'Wreck',
  obstruction: 'Obstruction',
  shallow: 'Shallow water',
  isolated_danger: 'Isolated danger',
};

/**
 * The tide to shade drying areas against, or null for chart datum.
 *
 * The drying-cover shading answers "is this bank covered right now", so on real
 * charts it must not be driven by the fictional Namley model: a made-up height
 * would paint a real drying bank as covered water. Null falls back to chart
 * datum, which shows the bank as drying, and being shown a hazard that has
 * actually covered is the right way round to be wrong.
 */
function dryingTideNowM(): number | null {
  const env = useEnvStore.getState();
  return heightNowM({
    providerId: useChartPlotterStore.getState().chartProviderId,
    heightSourceId: useTideSourceStore.getState().heightSourceId,
    portId: env.heightPortId,
    ms: env.streamTimeMs,
  });
}

/**
 * A short description of a saved ruler for the edit/delete prompt: total
 * distance, and the leg count when there is more than one. Naming what is about
 * to be deleted is most of what makes a confirm worth having.
 */
function measurementLabel(points: [number, number][]): string {
  let nm = 0;
  for (let i = 1; i < points.length; i += 1) nm += rhumbDistanceNm(points[i - 1], points[i]);
  const legs = Math.max(0, points.length - 1);
  const dist = `${nm.toFixed(2)} nm`;
  return legs > 1 ? `${dist} over ${legs} legs` : dist;
}

/**
 * The boat's measurements for the scaled hull, or null when we do not know
 * enough to draw one honestly. Falls back to the symbol in that case rather
 * than inventing a beam.
 */
function hullSettings(): { lengthM: number; beamM: number; fromBowM: number; fromCentreM: number } | null {
  const s = useSettingsStore.getState();
  // Length and beam are what the SIZE needs; the aerial position only refines
  // WHERE that shape sits. Requiring all three meant entering a length and a
  // beam and seeing nothing change, with no way to tell which field was
  // missing (fb-mscw4w5r-0). Unset, we assume the aerial is amidships, which is
  // what a plotter does before it is told otherwise, and the settings hint says
  // that setting it makes the placement exact. The anchor keeps its own,
  // stricter rule: no offset means it drops at the fix, unmoved.
  if (s.lengthM == null || s.beamM == null) return null;
  return {
    lengthM: s.lengthM,
    beamM: s.beamM,
    fromBowM: s.gnssFromBowM ?? s.lengthM / 2,
    fromCentreM: s.gnssFromCentreM ?? 0,
  };
}

function defaultGroupVisibility(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const l of LAYERS) {
    out[l.group] = true;
  }
  return out;
}

function distanceBetween(a: [number, number], b: [number, number]): number {
  const dy = (b[1] - a[1]) * 60;
  const dx = (b[0] - a[0]) * 60 * Math.cos((a[1] * Math.PI) / 180);
  return Math.hypot(dx, dy);
}
function bearingBetween(a: [number, number], b: [number, number]): number {
  const dy = b[1] - a[1];
  const dx = (b[0] - a[0]) * Math.cos((a[1] * Math.PI) / 180);
  const rad = Math.atan2(dx, dy);
  return ((rad * 180) / Math.PI + 360) % 360;
}

/**
 * Perpendicular distance (nm) from point p to the segment a-b, plus whether the
 * foot of the perpendicular lies within the segment. Flat-earth approximation,
 * which is fine at the scale of a single leg.
 */
function pointToLeg(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): { distNm: number; within: boolean } {
  const k = Math.cos((a[1] * Math.PI) / 180);
  const ax = a[0] * k;
  const ay = a[1];
  const bx = b[0] * k;
  const by = b[1];
  const px = p[0] * k;
  const py = p[1];
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  let t = len2 === 0 ? 0 : ((px - ax) * vx + (py - ay) * vy) / len2;
  const within = t >= -0.0001 && t <= 1.0001;
  t = Math.max(0, Math.min(1, t));
  const fx = ax + t * vx;
  const fy = ay + t * vy;
  return { distNm: Math.hypot(px - fx, py - fy) * 60, within };
}

/**
 * Index at which to insert a waypoint so it lands on the nearest leg the tap is
 * actually over. Returns the index of the new waypoint (after the leg's start),
 * or null if no leg's perpendicular foot contains the point.
 */
function nearestInsertIndex(positions: [number, number][], p: [number, number]): number | null {
  let bestDist = Infinity;
  let bestIdx = -1;
  for (let i = 0; i < positions.length - 1; i++) {
    const seg = pointToLeg(p, positions[i], positions[i + 1]);
    if (!seg.within) continue;
    if (seg.distNm < bestDist) {
      bestDist = seg.distNm;
      bestIdx = i + 1;
    }
  }
  return bestIdx === -1 ? null : bestIdx;
}
